import {
  CanvasRenderer,
  UIBaseElement,
  type Camera2d,
} from "melonjs";
import {
  computeRpgBattleEnemySlots,
  computeRpgBattleLayout,
  computeRpgCharacterWindowLayout,
  computeRpgInventoryGridLayout,
  computeRpgMapLayout,
  quoteRpgBulkSale,
  RpgBattlePacer,
  type RpgBattleLayout,
  type RpgMapLayout,
} from "@h5-rpg/app-shell";
import { RpgSkillAnimationTimeline } from "@h5-rpg/ui-melon";

import type {
  V1AffixRoll,
  V1DerivedEquipment,
  V1GameEngine,
  V1GameSnapshot,
} from "./engine";
import {
  V1_EQUIPMENT_SLOTS,
  v1EquipmentSlot,
  v1PrimaryEquipmentSlot,
} from "./equipment-slots";
import type { V1Item, V1Skill, V1Trait } from "./types";

interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface HitRegion {
  id: string;
  rect: Rect;
  run: () => void;
}

export interface V1AtlasSet {
  actors: HTMLImageElement;
  combatEffects: HTMLImageElement[];
  equipment: HTMLImageElement;
  items: HTMLImageElement;
  monsters: HTMLImageElement[];
  portals: HTMLImageElement;
  skills: HTMLImageElement[];
  tiles: HTMLImageElement;
}

const COLORS = {
  background: "#09080c",
  panel: "rgba(25,19,26,.96)",
  panelSoft: "rgba(38,29,38,.94)",
  border: "#7e5b3b",
  gold: "#e9b966",
  text: "#f6ead8",
  muted: "#b9aa97",
  hp: "#d45564",
  mp: "#478fd0",
  success: "#5fba87",
  danger: "#ef6a64",
};

const QUALITY_COLORS = ["#FFFFFF", "#36B54A", "#4E7EFF", "#B05FE9", "#FFA927", "#FF4F4F"];

const battlePlayerRect = (layout: RpgBattleLayout): Rect => {
  const size = layout.mode === "portrait"
    ? Math.min(150, layout.playerArea.height - 54)
    : Math.min(230, layout.playerArea.height * 0.58);
  return layout.mode === "portrait"
    ? {
        x: layout.playerArea.x + 12,
        y: layout.playerArea.y + 18,
        width: size,
        height: size,
      }
    : {
        x: layout.playerArea.x + layout.playerArea.width * 0.5 - size / 2,
        y: layout.playerArea.y + Math.max(20, layout.playerArea.height * 0.08),
        width: size,
        height: size,
      };
};

const contains = (rect: Rect, x: number, y: number) =>
  x >= rect.x && y >= rect.y && x <= rect.x + rect.width && y <= rect.y + rect.height;

const roundedPath = (context: CanvasRenderingContext2D, rect: Rect, radius = 10): void => {
  context.beginPath();
  context.roundRect(rect.x, rect.y, rect.width, rect.height, radius);
};

const panel = (
  context: CanvasRenderingContext2D,
  rect: Rect,
  fill = COLORS.panel,
  stroke = COLORS.border,
  radius = 12,
): void => {
  roundedPath(context, rect, radius);
  context.fillStyle = fill;
  context.fill();
  context.strokeStyle = stroke;
  context.lineWidth = 1.5;
  context.stroke();
};

const text = (
  context: CanvasRenderingContext2D,
  value: string,
  x: number,
  y: number,
  options: {
    align?: CanvasTextAlign;
    color?: string;
    font?: string;
    maxWidth?: number;
  } = {},
): void => {
  context.font = options.font ?? "500 16px 'PingFang SC','Microsoft YaHei',sans-serif";
  context.fillStyle = options.color ?? COLORS.text;
  context.textAlign = options.align ?? "left";
  context.textBaseline = "alphabetic";
  if (options.maxWidth) context.fillText(value, x, y, options.maxWidth);
  else context.fillText(value, x, y);
};

const wrap = (
  context: CanvasRenderingContext2D,
  value: string,
  x: number,
  y: number,
  width: number,
  lineHeight = 22,
  maxLines = 4,
): void => {
  context.save();
  context.textAlign = "left";
  context.textBaseline = "alphabetic";
  let line = "";
  let lineIndex = 0;
  for (const character of [...value]) {
    const candidate = line + character;
    if (line && context.measureText(candidate).width > width) {
      context.fillText(line, x, y + lineIndex * lineHeight);
      lineIndex += 1;
      line = character;
      if (lineIndex >= maxLines - 1) break;
    } else {
      line = candidate;
    }
  }
  if (line && lineIndex < maxLines) context.fillText(line, x, y + lineIndex * lineHeight);
  context.restore();
};

const bar = (
  context: CanvasRenderingContext2D,
  rect: Rect,
  value: number,
  maximum: number,
  color: string,
): void => {
  roundedPath(context, rect, rect.height / 2);
  context.fillStyle = "#0b090d";
  context.fill();
  const ratio = maximum > 0 ? Math.max(0, Math.min(1, value / maximum)) : 0;
  if (ratio > 0) {
    roundedPath(context, { ...rect, width: Math.max(3, rect.width * ratio) }, rect.height / 2);
    context.fillStyle = color;
    context.fill();
  }
};

const drawAtlas = (
  context: CanvasRenderingContext2D,
  image: HTMLImageElement | undefined,
  index: number,
  rect: Rect,
  columns = 4,
  rows = 4,
): void => {
  if (!image?.complete || image.naturalWidth === 0) return;
  const sourceWidth = image.naturalWidth / columns;
  const sourceHeight = image.naturalHeight / rows;
  context.save();
  roundedPath(context, rect, Math.min(10, rect.width / 5));
  context.clip();
  context.drawImage(
    image,
    (index % columns) * sourceWidth,
    Math.floor(index / columns) * sourceHeight,
    sourceWidth,
    sourceHeight,
    rect.x,
    rect.y,
    rect.width,
    rect.height,
  );
  context.restore();
};

const drawTileAtlas = (
  context: CanvasRenderingContext2D,
  image: HTMLImageElement | undefined,
  index: number,
  rect: Rect,
  flipX = false,
  flipY = false,
): void => {
  if (!image?.complete || image.naturalWidth === 0) return;
  const columns = 4;
  const rows = 4;
  const sourceWidth = image.naturalWidth / columns;
  const sourceHeight = image.naturalHeight / rows;
  // Atlas cells retain a narrow divider at the montage edge. Crop it before
  // scaling so repeated terrain has no visible grid.
  const inset = 5;
  context.save();
  context.translate(rect.x + rect.width / 2, rect.y + rect.height / 2);
  context.scale(flipX ? -1 : 1, flipY ? -1 : 1);
  context.drawImage(
    image,
    (index % columns) * sourceWidth + inset,
    Math.floor(index / columns) * sourceHeight + inset,
    sourceWidth - inset * 2,
    sourceHeight - inset * 2,
    -rect.width / 2,
    -rect.height / 2,
    rect.width,
    rect.height,
  );
  context.restore();
};

export class V1GameSurface extends UIBaseElement {
  private hitRegions: HitRegion[] = [];
  private inventoryPage = 0;
  private selectedEquipmentInstanceId: string | null = null;
  private selectedEquipmentAnchor: Rect | null = null;
  private selectedItemId: number | null = null;
  private selectedItemAnchor: Rect | null = null;
  private selectedSkillId: number | null = null;
  private skillPage = 0;
  private equipmentSkillsOpen = false;
  private selectedEquipmentSkillId: number | null = null;
  private equipmentSkillPage = 0;
  private shopScrollRow = 0;
  private readonly skillAnimation = new RpgSkillAnimationTimeline();
  private readonly battlePacer = new RpgBattlePacer();
  private battleActionCooldownMs = 0;
  private battleMenu: "skills" | "items" | null = null;
  private bulkSaleOpen = false;
  private readonly bulkSaleQualityIds = new Set<number>([1]);
  private mapPath: Array<{ dx: number; dy: number }> = [];
  private mapPathElapsedMs = 0;
  private mapPathAction: (() => void) | null = null;
  private mapPathTarget: { mapId: number; x: number; y: number } | null = null;

  constructor(
    private readonly engine: V1GameEngine,
    private readonly logicalWidth: number,
    private readonly logicalHeight: number,
    private readonly atlases: V1AtlasSet,
  ) {
    super(0, 0, logicalWidth, logicalHeight);
    this.anchorPoint.set(0, 0);
    this.alwaysUpdate = true;
    this.isClickable = false;
  }

  override update(deltaMs: number): boolean {
    const speed = this.engine.snapshot.battle ? this.battlePacer.speed() : 1;
    this.engine.tick(deltaMs * speed);
    this.updateMapPath(deltaMs);
    const battle = this.engine.snapshot.battle;
    if (!battle) this.battleMenu = null;
    this.battleActionCooldownMs = Math.max(0, this.battleActionCooldownMs - deltaMs * speed);
    this.battlePacer.update(deltaMs, {
      active: battle?.status === "active" && !battle.pendingEnemyTurn,
      auto: battle?.auto === true,
      intervalMs: 1_550,
      performAction: () => this.performAutoBattleAction(),
    });
    this.skillAnimation.sync(this.engine.snapshot.combatFx);
    this.skillAnimation.update(
      deltaMs * speed,
    );
    return true;
  }

  private performBattleAction(): boolean {
    if (this.battleActionCooldownMs > 0) return false;
    const performed = this.engine.performAttack();
    if (performed) this.battleActionCooldownMs = 1_150;
    return performed;
  }

  private performAutoBattleAction(): boolean {
    if (this.battleActionCooldownMs > 0) return false;
    const performed = this.engine.performAutoBattleAction();
    if (performed) this.battleActionCooldownMs = 1_250;
    return performed;
  }

  override draw(renderer: CanvasRenderer, _viewport?: Camera2d): void {
    const context = renderer.getContext() as CanvasRenderingContext2D;
    const snapshot = this.engine.snapshot;
    this.hitRegions = [];
    context.save();
    context.imageSmoothingEnabled = true;
    context.fillStyle = COLORS.background;
    context.fillRect(0, 0, this.logicalWidth, this.logicalHeight);
    if (!snapshot.started) this.drawTitle(context);
    else if (snapshot.battle) this.drawBattle(context, snapshot);
    else this.drawMap(context, snapshot);
    if (snapshot.openPanel === "inventory") this.drawInventory(context, snapshot);
    if (snapshot.openPanel === "skills") this.drawSkills(context, snapshot);
    if (snapshot.openPanel === "map") this.drawWorldMap(context);
    if (snapshot.openPanel === "shop") this.drawShop(context, snapshot);
    if (snapshot.loot) this.drawLoot(context, snapshot);
    if (snapshot.dialogue) this.drawDialogue(context, snapshot);
    if (snapshot.toast) this.drawToast(context, snapshot.toast);
    context.restore();
  }

  handleTap(x: number, y: number): boolean {
    const candidates = this.equipmentSkillsOpen
      ? this.hitRegions.filter((entry) => entry.id.startsWith("equipment-skills:"))
      : this.bulkSaleOpen
      ? this.hitRegions.filter((entry) => entry.id.startsWith("bulk-sale:"))
      : this.selectedEquipmentInstanceId
        ? this.hitRegions.filter((entry) =>
            entry.id.startsWith("equipment-detail:")
          )
        : this.selectedItemId
          ? this.hitRegions.filter((entry) => entry.id.startsWith("item-detail:"))
        : this.hitRegions;
    const region = [...candidates].reverse().find((entry) => contains(entry.rect, x, y));
    if (!region) {
      if (this.engine.snapshot.dialogue) {
        this.engine.closeDialogue();
        return true;
      }
      return false;
    }
    if (!region.id.startsWith("map:")) this.clearMapPath();
    region.run();
    return true;
  }

  handleSwipe(deltaX: number, deltaY: number): boolean {
    if (Math.max(Math.abs(deltaX), Math.abs(deltaY)) < 26) return false;
    this.clearMapPath();
    if (this.engine.snapshot.openPanel === "shop") {
      if (Math.abs(deltaY) >= Math.abs(deltaX)) this.scrollShop(deltaY < 0 ? 1 : -1);
      return true;
    }
    if (Math.abs(deltaX) > Math.abs(deltaY)) return this.engine.move(deltaX > 0 ? 1 : -1, 0);
    return this.engine.move(0, deltaY > 0 ? 1 : -1);
  }

  handleWheel(deltaY: number): boolean {
    if (this.engine.snapshot.openPanel !== "shop" || deltaY === 0) return false;
    this.scrollShop(deltaY > 0 ? 1 : -1);
    return true;
  }

  handleKey(key: string): boolean {
    this.clearMapPath();
    if (this.engine.snapshot.loot && (key === "Enter" || key === " ")) {
      this.engine.collectLoot();
      return true;
    }
    if (this.engine.snapshot.dialogue && (key === "Enter" || key === " " || key.toLowerCase() === "e")) {
      this.engine.closeDialogue();
      return true;
    }
    if (key === "Escape") {
      if (this.equipmentSkillsOpen) {
        this.equipmentSkillsOpen = false;
        return true;
      }
      if (this.bulkSaleOpen) {
        this.bulkSaleOpen = false;
        return true;
      }
      if (this.selectedEquipmentInstanceId) {
        this.selectedEquipmentInstanceId = null;
        this.selectedEquipmentAnchor = null;
        return true;
      }
      if (this.selectedItemId) {
        this.selectedItemId = null;
        this.selectedItemAnchor = null;
        return true;
      }
      if (this.engine.snapshot.openPanel) this.engine.closePanel();
      else this.engine.enterTitle();
      return true;
    }
    const normalized = key.toLowerCase();
    if (normalized === "arrowup" || normalized === "w") return this.engine.move(0, -1);
    if (normalized === "arrowdown" || normalized === "s") return this.engine.move(0, 1);
    if (normalized === "arrowleft" || normalized === "a") return this.engine.move(-1, 0);
    if (normalized === "arrowright" || normalized === "d") return this.engine.move(1, 0);
    if (normalized === "e" || key === "Enter") return this.engine.interact();
    if (normalized === "b") {
      this.equipmentSkillsOpen = false;
      if (this.bulkSaleOpen) {
        this.bulkSaleOpen = false;
        return true;
      }
      this.selectedEquipmentInstanceId = null;
      this.selectedEquipmentAnchor = null;
      this.selectedItemId = null;
      this.selectedItemAnchor = null;
      this.engine.togglePanel("inventory");
      return true;
    }
    if (normalized === "k") {
      this.engine.togglePanel("skills");
      return true;
    }
    if (key === " " && this.engine.snapshot.battle) return this.performBattleAction();
    return false;
  }

  private clearMapPath(): void {
    this.mapPath = [];
    this.mapPathElapsedMs = 0;
    this.mapPathAction = null;
    this.mapPathTarget = null;
  }

  private updateMapPath(deltaMs: number): void {
    if (this.mapPath.length === 0) return;
    const snapshot = this.engine.snapshot;
    if (
      !snapshot.started
      || snapshot.battle
      || snapshot.loot
      || snapshot.openPanel
      || snapshot.dialogue
    ) {
      this.clearMapPath();
      return;
    }
    this.mapPathElapsedMs += deltaMs;
    if (this.mapPathElapsedMs < 115) return;
    this.mapPathElapsedMs = 0;
    const step = this.mapPath.shift();
    if (!step || !this.engine.move(step.dx, step.dy)) {
      this.clearMapPath();
      return;
    }
    if (this.engine.snapshot.battle || this.engine.currentMap().id !== this.mapPathTarget?.mapId) {
      this.clearMapPath();
      return;
    }
    if (this.mapPath.length === 0) {
      const action = this.mapPathAction;
      this.clearMapPath();
      action?.();
    }
  }

  private queueMapPath(targetX: number, targetY: number): void {
    this.clearMapPath();
    const map = this.engine.currentMap();
    const cell = map.cells[targetY]?.[targetX];
    if (!cell) return;
    let path: Array<{ dx: number; dy: number }> = [];
    let action: (() => void) | null = null;
    if (cell.special?.type === 3 || cell.special?.type === 4) {
      const candidates = ([
        [1, 0], [-1, 0], [0, 1], [0, -1],
      ] as const).map(([dx, dy]) => {
        const x = targetX + dx;
        const y = targetY + dy;
        const alreadyThere = x === this.engine.snapshot.player.x && y === this.engine.snapshot.player.y;
        const candidatePath = alreadyThere ? [] : this.engine.pathTo(x, y, false);
        return { x, y, path: candidatePath, reachable: alreadyThere || candidatePath.length > 0 };
      }).filter((entry) => entry.reachable).sort((left, right) => left.path.length - right.path.length);
      const destination = candidates[0];
      if (!destination) return;
      path = destination.path;
      action = () => this.engine.interactAt(targetX, targetY);
    } else {
      path = this.engine.pathTo(targetX, targetY, true);
      if (path.length === 0) return;
    }
    this.mapPath = [...path];
    this.mapPathAction = action;
    this.mapPathTarget = { mapId: map.id, x: targetX, y: targetY };
    if (this.mapPath.length === 0) {
      const immediate = this.mapPathAction;
      this.clearMapPath();
      immediate?.();
    }
  }

  private addHit(id: string, rect: Rect, run: () => void): void {
    this.hitRegions.push({ id, rect, run });
  }

  private button(
    context: CanvasRenderingContext2D,
    id: string,
    rect: Rect,
    label: string,
    run: () => void,
    accent = COLORS.border,
  ): void {
    panel(context, rect, "rgba(36,28,37,.98)", accent, 10);
    text(context, label, rect.x + rect.width / 2, rect.y + rect.height / 2 + 6, {
      align: "center",
      color: COLORS.text,
      font: "600 15px 'PingFang SC',sans-serif",
      maxWidth: rect.width - 10,
    });
    this.addHit(id, rect, run);
  }

  private drawTitle(context: CanvasRenderingContext2D): void {
    const portrait = this.logicalHeight > this.logicalWidth;
    const centerX = this.logicalWidth / 2;
    const centerY = portrait ? this.logicalHeight * 0.44 : this.logicalHeight * 0.42;
    const glow = context.createRadialGradient(centerX, centerY, 10, centerX, centerY, Math.max(this.logicalWidth, this.logicalHeight) * 0.55);
    glow.addColorStop(0, "#34201a");
    glow.addColorStop(1, COLORS.background);
    context.fillStyle = glow;
    context.fillRect(0, 0, this.logicalWidth, this.logicalHeight);
    text(context, "烬 途", centerX, centerY - 80, {
      align: "center",
      color: COLORS.gold,
      font: `700 ${portrait ? 58 : 70}px 'Songti SC',serif`,
    });
    text(context, "游戏 2 · 阶段品质成长", centerX, centerY - 35, {
      align: "center",
      color: COLORS.muted,
      font: "500 18px 'PingFang SC',sans-serif",
    });
    const width = portrait ? 260 : 300;
    this.button(context, "title:new", { x: centerX - width / 2, y: centerY + 20, width, height: 54 }, "开始新旅程", () => this.engine.newGame(), COLORS.gold);
    if (this.engine.hasSave()) {
      this.button(context, "title:resume", { x: centerX - width / 2, y: centerY + 86, width, height: 50 }, "继续存档", () => this.engine.resume());
    }
    text(context, "WASD / 方向键移动 · E 交互 · B 背包 · K 技能", centerX, this.logicalHeight - 28, {
      align: "center",
      color: COLORS.muted,
      font: "500 13px 'PingFang SC',sans-serif",
    });
  }

  private drawMap(context: CanvasRenderingContext2D, snapshot: V1GameSnapshot): void {
    const map = this.engine.currentMap();
    const layout = computeRpgMapLayout(
      this.logicalWidth,
      this.logicalHeight,
      map.width,
      map.height,
      { x: snapshot.player.x, y: snapshot.player.y },
    );
    context.fillStyle = "#100d12";
    context.fillRect(0, 0, this.logicalWidth, this.logicalHeight);
    this.drawGrid(context, layout);
    this.drawMapHud(context, snapshot, layout);
    this.drawMapNavigation(context, snapshot, layout);
  }

  private drawMapHud(
    context: CanvasRenderingContext2D,
    snapshot: V1GameSnapshot,
    layout: RpgMapLayout,
  ): void {
    const map = this.engine.currentMap();
    const stats = this.engine.playerStats();
    const player = snapshot.player;
    context.fillStyle = "rgba(12,9,13,.96)";
    context.fillRect(0, 0, layout.width, layout.hudHeight);
    context.strokeStyle = "rgba(157,111,67,.55)";
    context.beginPath();
    context.moveTo(0, layout.hudHeight - 1);
    context.lineTo(layout.width, layout.hudHeight - 1);
    context.stroke();
    text(context, map.name, 24, 30, {
      color: COLORS.gold,
      font: "700 20px 'Songti SC',serif",
    });
    text(context, `${map.width}×${map.height} · 地图 ${map.id}/39`, 24, 52, {
      color: COLORS.muted,
      font: "500 11px 'PingFang SC',sans-serif",
    });
    if (layout.mode === "landscape") {
      text(context, `${this.engine.content.actor.name}  Lv.${player.level}`, 280, 28, {
        color: COLORS.text,
        font: "700 15px sans-serif",
      });
      text(context, `HP ${player.hp}/${stats.maxHp}`, 280, 50, {
        color: COLORS.hp,
        font: "600 12px sans-serif",
      });
      bar(context, { x: 390, y: 39, width: 220, height: 10 }, player.hp, stats.maxHp, COLORS.hp);
      text(context, `MP ${player.mp}/${stats.maxMp}`, 634, 50, {
        color: COLORS.mp,
        font: "600 12px sans-serif",
      });
      bar(context, { x: 744, y: 39, width: 150, height: 10 }, player.mp, stats.maxMp, COLORS.mp);
      text(context, `攻 ${stats.attack}  防 ${stats.defense}  魔攻 ${stats.magicAttack}  魔防 ${stats.magicDefense}`, layout.width - 24, 27, {
        align: "right",
        color: COLORS.text,
        font: "600 12px sans-serif",
      });
      text(context, `金币 ${player.gold}  背包装备 ${player.inventory.length}`, layout.width - 24, 51, {
        align: "right",
        color: COLORS.gold,
        font: "600 12px sans-serif",
      });
    } else {
      text(context, `Lv.${player.level}  HP ${player.hp}/${stats.maxHp}  MP ${player.mp}/${stats.maxMp}`, layout.width - 18, 30, {
        align: "right",
        color: COLORS.text,
        font: "600 11px sans-serif",
      });
      text(context, `攻 ${stats.attack}  防 ${stats.defense}  金币 ${player.gold}`, layout.width - 18, 53, {
        align: "right",
        color: COLORS.gold,
        font: "600 11px sans-serif",
      });
    }
  }

  private drawMapNavigation(
    context: CanvasRenderingContext2D,
    snapshot: V1GameSnapshot,
    layout: RpgMapLayout,
  ): void {
    const y = layout.height - layout.navigationHeight;
    context.fillStyle = "rgba(12,9,13,.97)";
    context.fillRect(0, y, layout.width, layout.navigationHeight);
    context.strokeStyle = "rgba(157,111,67,.55)";
    context.beginPath();
    context.moveTo(0, y + 1);
    context.lineTo(layout.width, y + 1);
    context.stroke();
    if (layout.mode === "landscape") {
      text(context, "WASD / 方向键移动 · E 交互", 24, y + 43, {
        color: COLORS.muted,
        font: "500 13px sans-serif",
      });
      const gap = 8;
      const buttonWidth = 104;
      const startX = layout.width - 24 - buttonWidth * 4 - gap * 3;
      this.button(context, "nav:inventory", { x: startX, y: y + 12, width: buttonWidth, height: 46 }, "角色背包 (B)", () => this.engine.togglePanel("inventory"));
      this.button(context, "nav:skills", { x: startX + (buttonWidth + gap), y: y + 12, width: buttonWidth, height: 46 }, "技能 (K)", () => this.engine.togglePanel("skills"));
      this.button(context, "nav:save", { x: startX + (buttonWidth + gap) * 2, y: y + 12, width: buttonWidth, height: 46 }, "保存", () => this.engine.save());
      this.button(context, "nav:load", { x: startX + (buttonWidth + gap) * 3, y: y + 12, width: buttonWidth, height: 46 }, "读取存档", () => this.engine.loadSave(), this.engine.hasSave() ? COLORS.gold : "#4b3d40");
    } else {
      const gap = 6;
      const buttonWidth = (layout.width - 28 - gap * 3) / 4;
      const startX = 14;
      this.button(context, "nav:inventory", { x: startX, y: y + 12, width: buttonWidth, height: 50 }, "背包", () => this.engine.togglePanel("inventory"));
      this.button(context, "nav:skills", { x: startX + buttonWidth + gap, y: y + 12, width: buttonWidth, height: 50 }, "技能", () => this.engine.togglePanel("skills"));
      this.button(context, "nav:save", { x: startX + (buttonWidth + gap) * 2, y: y + 12, width: buttonWidth, height: 50 }, "保存", () => this.engine.save());
      this.button(context, "nav:load", { x: startX + (buttonWidth + gap) * 3, y: y + 12, width: buttonWidth, height: 50 }, "读档", () => this.engine.loadSave(), this.engine.hasSave() ? COLORS.gold : "#4b3d40");
    }
    if (snapshot.firstBossDefeated) {
      text(context, "✓ 第一首领已击败", 24, y + 64, {
        color: COLORS.success,
        font: "700 11px sans-serif",
      });
    }
  }

  private drawGrid(context: CanvasRenderingContext2D, layout: RpgMapLayout): void {
    const map = this.engine.currentMap();
    const viewport = layout.mapViewport;
    const cellSize = viewport.cellSize;
    const startX = viewport.originX;
    const startY = viewport.originY;
    const visibleX = viewport.columns;
    const visibleY = viewport.rows;
    const renderedColumns = Math.min(map.width, visibleX);
    const renderedRows = Math.min(map.height, visibleY);
    const screenOriginX =
      viewport.x +
      Math.max(0, (viewport.width - renderedColumns * cellSize) / 2);
    const screenOriginY =
      viewport.y +
      Math.max(0, (viewport.height - renderedRows * cellSize) / 2);
    context.save();
    context.beginPath();
    context.rect(viewport.x, viewport.y, viewport.width, viewport.height);
    context.clip();
    const monsters = new Map(this.engine.monstersOnCurrentMap().map((entry) => [`${entry.x}:${entry.y}`, entry]));
    const bosses = new Map(this.engine.livingBossesOnCurrentMap().map((entry) => [`${entry.x}:${entry.y}`, entry]));
    for (let viewY = 0; viewY < visibleY; viewY += 1) {
      for (let viewX = 0; viewX < visibleX; viewX += 1) {
        const worldX = startX + viewX;
        const worldY = startY + viewY;
        if (worldX >= map.width || worldY >= map.height) continue;
        const cell = map.cells[worldY]?.[worldX];
        if (!cell) continue;
        const tileRect = { x: screenOriginX + viewX * cellSize, y: screenOriginY + viewY * cellSize, width: cellSize + 0.5, height: cellSize + 0.5 };
        const tileIndex = cell.terrain === 1 ? 8 + ((map.tilesetId - 1) % 8) : (map.tilesetId - 1) % 8;
        drawTileAtlas(
          context,
          this.atlases.tiles,
          tileIndex,
          tileRect,
          cell.terrain === 0 && worldX % 2 === 1,
          cell.terrain === 0 && worldY % 2 === 1,
        );
        context.fillStyle = cell.terrain === 1 ? "rgba(10,8,12,.40)" : "rgba(21,16,22,.12)";
        context.fillRect(tileRect.x, tileRect.y, tileRect.width, tileRect.height);
        const special = cell.special;
        if (special?.type === 1) {
          const targetMap = this.engine.content.indexes.maps.get(special.targetMapId);
          const targetTilesetId = targetMap?.tilesetId ?? map.tilesetId;
          const portalIndex = Math.max(0, Math.min(7, targetTilesetId - 1));
          if (this.atlases.portals.complete && this.atlases.portals.naturalWidth > 0) {
            drawAtlas(context, this.atlases.portals, portalIndex, {
              x: tileRect.x + 1,
              y: tileRect.y + 1,
              width: cellSize - 2,
              height: cellSize - 2,
            }, 4, 2);
          } else {
            context.fillStyle = COLORS.gold;
            context.beginPath();
            context.moveTo(tileRect.x + cellSize / 2, tileRect.y + 6);
            context.lineTo(tileRect.x + cellSize - 6, tileRect.y + cellSize / 2);
            context.lineTo(tileRect.x + cellSize / 2, tileRect.y + cellSize - 6);
            context.lineTo(tileRect.x + 6, tileRect.y + cellSize / 2);
            context.closePath();
            context.fill();
          }
          const destination = `→${targetMap?.name ?? `地图${special.targetMapId}`}`;
          const labelFontSize = Math.max(8, Math.min(11, cellSize * 0.18));
          const labelY = tileRect.y + cellSize - 3;
          context.save();
          context.font = `700 ${labelFontSize}px 'PingFang SC',sans-serif`;
          const labelWidth = Math.min(
            cellSize - 4,
            context.measureText(destination).width + 8,
          );
          context.fillStyle = "rgba(8,6,10,.82)";
          context.fillRect(
            tileRect.x + (cellSize - labelWidth) / 2,
            labelY - labelFontSize - 3,
            labelWidth,
            labelFontSize + 5,
          );
          context.restore();
          text(context, destination, tileRect.x + cellSize / 2, labelY, {
            align: "center",
            color: COLORS.gold,
            font: `700 ${labelFontSize}px 'PingFang SC',sans-serif`,
            maxWidth: cellSize - 8,
          });
          if (special.goldCost > 0) {
            text(context, `${special.goldCost / 10_000}万金`, tileRect.x + cellSize / 2, tileRect.y + 11, {
              align: "center",
              color: "#fff1a8",
              font: `800 ${Math.max(8, labelFontSize - 1)}px 'PingFang SC',sans-serif`,
              maxWidth: cellSize - 6,
            });
          }
        } else if (special?.type === 3) {
          const npc = map.npcs.find((entry) => entry.id === special.localNpcId);
          if (npc) {
            drawAtlas(context, this.atlases.actors, npc.spriteIndex, {
              x: tileRect.x + 2,
              y: tileRect.y + 1,
              width: cellSize - 4,
              height: cellSize - 4,
            });
            text(context, npc.name, tileRect.x + cellSize / 2, tileRect.y + cellSize - 2, {
              align: "center",
              color: COLORS.gold,
              font: `700 ${Math.max(8, cellSize * 0.15)}px sans-serif`,
              maxWidth: cellSize - 4,
            });
          } else {
            context.fillStyle = "#f2d28a";
            context.beginPath();
            context.arc(tileRect.x + cellSize / 2, tileRect.y + cellSize / 2, cellSize * 0.22, 0, Math.PI * 2);
            context.fill();
            text(context, "NPC", tileRect.x + cellSize / 2, tileRect.y + cellSize - 3, {
              align: "center",
              color: COLORS.gold,
              font: `700 ${Math.max(8, cellSize * 0.19)}px sans-serif`,
            });
          }
        } else if (special?.type === 4) {
          drawAtlas(context, this.atlases.actors, 1, {
            x: tileRect.x + 2,
            y: tileRect.y + 1,
            width: cellSize - 4,
            height: cellSize - 4,
          });
          text(context, "商店", tileRect.x + cellSize / 2, tileRect.y + cellSize - 2, {
            align: "center",
            color: COLORS.success,
            font: `700 ${Math.max(8, cellSize * 0.15)}px sans-serif`,
            maxWidth: cellSize - 4,
          });
        }
        const monster = monsters.get(`${worldX}:${worldY}`);
        const boss = bosses.get(`${worldX}:${worldY}`);
        if (monster || boss) {
          const troopId = monster?.troopId ?? boss?.boss.troopId ?? 1;
          const troop = this.engine.content.indexes.troops.get(troopId);
          const enemy = troop ? this.engine.content.indexes.enemies.get(troop.members[0]?.enemyId ?? 1) : undefined;
          if (enemy) {
            drawAtlas(context, this.atlases.monsters[enemy.spriteSheet - 1], enemy.spriteIndex, {
              x: tileRect.x + 2, y: tileRect.y + 1, width: cellSize - 4, height: cellSize - 4,
            });
            text(context, boss ? "BOSS" : "敌", tileRect.x + cellSize / 2, tileRect.y + cellSize - 2, {
              align: "center", color: boss ? "#ff625c" : "#ffb0a8", font: `700 ${Math.max(8, cellSize * 0.18)}px sans-serif`,
            });
          }
        }
        const player = this.engine.snapshot.player;
        if (worldX === player.x && worldY === player.y) {
          drawAtlas(context, this.atlases.actors, 0, {
            x: tileRect.x + 1, y: tileRect.y + 1, width: cellSize - 2, height: cellSize - 2,
          });
          context.strokeStyle = COLORS.gold;
          context.lineWidth = 2;
          context.strokeRect(tileRect.x + 2, tileRect.y + 2, cellSize - 4, cellSize - 4);
        }
        if (
          this.mapPathTarget?.mapId === map.id
          && this.mapPathTarget.x === worldX
          && this.mapPathTarget.y === worldY
        ) {
          context.strokeStyle = "#72d7ff";
          context.lineWidth = 3;
          context.strokeRect(tileRect.x + 4, tileRect.y + 4, cellSize - 8, cellSize - 8);
        }
        this.addHit(`map:${worldX}:${worldY}`, tileRect, () => {
          this.clearMapPath();
          const dx = worldX - this.engine.snapshot.player.x;
          const dy = worldY - this.engine.snapshot.player.y;
          if (Math.abs(dx) + Math.abs(dy) === 1) {
            const moved = this.engine.move(dx, dy);
            if (
              !moved
              && (cell.special?.type === 3 || cell.special?.type === 4)
            ) {
              this.engine.interact();
            }
          } else {
            this.queueMapPath(worldX, worldY);
          }
        });
      }
    }
    context.restore();
  }

  private drawBattle(context: CanvasRenderingContext2D, snapshot: V1GameSnapshot): void {
    const battle = snapshot.battle;
    if (!battle) return;
    const layout = computeRpgBattleLayout(
      this.logicalWidth,
      this.logicalHeight,
      4,
    );
    const map = this.engine.currentMap();
    const backgroundTile = Math.abs(map.tilesetId - 1) % 16;
    const tileSize = layout.mode === "portrait" ? 150 : 210;
    for (let y = 0; y < this.logicalHeight; y += tileSize) {
      for (let x = 0; x < this.logicalWidth; x += tileSize) {
        const column = Math.floor(x / tileSize);
        const row = Math.floor(y / tileSize);
        drawTileAtlas(context, this.atlases.tiles, backgroundTile, {
          x, y, width: tileSize + 1, height: tileSize + 1,
        }, column % 2 === 1, row % 2 === 1);
      }
    }
    const shade = context.createLinearGradient(0, 0, 0, this.logicalHeight);
    shade.addColorStop(0, "rgba(8,6,10,.42)");
    shade.addColorStop(0.65, "rgba(10,7,11,.62)");
    shade.addColorStop(1, "rgba(7,5,8,.94)");
    context.fillStyle = shade;
    context.fillRect(0, 0, this.logicalWidth, this.logicalHeight);
    context.fillStyle = "rgba(11,8,12,.88)";
    context.fillRect(0, 0, layout.header.width, layout.header.height);
    context.strokeStyle = "rgba(218,168,93,.55)";
    context.beginPath();
    context.moveTo(0, layout.header.height - 1);
    context.lineTo(layout.header.width, layout.header.height - 1);
    context.stroke();
    text(context, `${battle.kind === "boss" ? "首领战" : "遭遇战"} · ${map.name}`, 20, 38, {
      color: battle.kind === "boss" ? COLORS.danger : COLORS.gold,
      font: "700 23px 'Songti SC',serif",
    });
    text(context, `第 ${battle.round} 回合`, this.logicalWidth - 20, 38, { align: "right", color: COLORS.muted });
    const animationFrame = this.skillAnimation.frame();
    const lunge = animationFrame?.active && animationFrame.targetIndex >= 0
      ? Math.sin(Math.min(1, animationFrame.progress / 0.62) * Math.PI) * (layout.mode === "portrait" ? 0 : 42)
      : 0;
    const heroRect = battlePlayerRect(layout);
    heroRect.x += lunge;
    context.save();
    context.fillStyle = "rgba(0,0,0,.42)";
    context.beginPath();
    context.ellipse(
      heroRect.x + heroRect.width / 2,
      heroRect.y + heroRect.height * 0.88,
      heroRect.width * 0.42,
      heroRect.height * 0.1,
      0,
      0,
      Math.PI * 2,
    );
    context.fill();
    context.shadowColor = "rgba(234,185,102,.32)";
    context.shadowBlur = 24;
    drawAtlas(context, this.atlases.actors, 0, heroRect);
    context.restore();
    const enemySlots = computeRpgBattleEnemySlots(layout, battle.enemies.length);
    battle.enemies.forEach((unit, index) => {
      const rect = enemySlots[index];
      if (!rect) return;
      const selected = unit.index === battle.selectedIndex;
      const isImpactTarget =
        animationFrame?.active
        && animationFrame.targetIndex === unit.index
        && (animationFrame.phase === "impact" || animationFrame.phase === "release");
      const shake = isImpactTarget
        ? Math.sin(animationFrame.progress * Math.PI * 18) * 8
        : 0;
      const enemy = this.engine.content.indexes.enemies.get(unit.enemyId);
      context.save();
      context.fillStyle = selected ? "rgba(231,185,102,.25)" : "rgba(0,0,0,.38)";
      context.beginPath();
      context.ellipse(
        rect.x + rect.width / 2,
        rect.y + rect.height * 0.77,
        rect.width * (selected ? 0.46 : 0.39),
        rect.height * 0.08,
        0,
        0,
        Math.PI * 2,
      );
      context.fill();
      if (selected) {
        context.strokeStyle = COLORS.gold;
        context.lineWidth = 2;
        context.stroke();
      }
      context.shadowColor = unit.rank === 3 ? "rgba(239,106,100,.5)" : "rgba(0,0,0,.55)";
      context.shadowBlur = unit.rank === 3 ? 28 : 16;
      drawAtlas(context, enemy ? this.atlases.monsters[enemy.spriteSheet - 1] : undefined, enemy?.spriteIndex ?? 0, {
        x: rect.x + 8 + shake,
        y: rect.y,
        width: rect.width - 16,
        height: rect.height - 72,
      });
      context.restore();
      text(context, `${unit.rank === 3 ? "BOSS · " : ""}${unit.name}`, rect.x + rect.width / 2, rect.y + rect.height - 46, {
        align: "center", color: unit.rank === 3 ? COLORS.danger : COLORS.text, font: "700 14px sans-serif", maxWidth: rect.width - 10,
      });
      bar(context, { x: rect.x + 12, y: rect.y + rect.height - 31, width: rect.width - 24, height: 10 }, unit.hp, unit.maxHp, COLORS.hp);
      text(context, `${unit.hp}/${unit.maxHp}`, rect.x + rect.width / 2, rect.y + rect.height - 8, { align: "center", color: COLORS.text, font: "600 11px sans-serif" });
      this.addHit(`battle:enemy:${unit.index}`, rect, () => this.engine.selectEnemy(unit.index));
    });
    this.drawSkillAnimation(context, layout, snapshot);
    const stats = this.engine.playerStats();
    if (layout.mode === "portrait") {
      const playerStatus = {
        x: heroRect.x + heroRect.width + 14,
        y: layout.playerArea.y + 28,
        width: Math.max(140, layout.playerArea.x + layout.playerArea.width - heroRect.x - heroRect.width - 28),
      };
      text(context, this.engine.content.actor.name, playerStatus.x, playerStatus.y + 17, {
        color: COLORS.text, font: "700 16px sans-serif",
      });
      text(context, `HP ${snapshot.player.hp}/${stats.maxHp}`, playerStatus.x, playerStatus.y + 42, {
        color: COLORS.text, font: "600 13px sans-serif",
      });
      bar(context, {
        x: playerStatus.x,
        y: playerStatus.y + 49,
        width: playerStatus.width,
        height: 12,
      }, snapshot.player.hp, stats.maxHp, COLORS.hp);
      if (stats.maxMp > 0) {
        text(context, `MP ${snapshot.player.mp}/${stats.maxMp}`, playerStatus.x, playerStatus.y + 82, {
          color: COLORS.muted, font: "600 12px sans-serif",
        });
        bar(context, {
          x: playerStatus.x,
          y: playerStatus.y + 89,
          width: playerStatus.width,
          height: 7,
        }, snapshot.player.mp, stats.maxMp, COLORS.mp);
      }
    } else {
      const statusWidth = Math.min(340, layout.playerStatus.width);
      text(context, `${this.engine.content.actor.name}  HP ${snapshot.player.hp}/${stats.maxHp}  MP ${snapshot.player.mp}/${stats.maxMp}`, layout.playerStatus.x, layout.playerStatus.y + 17, {
        color: COLORS.text, font: "700 16px sans-serif",
      });
      bar(context, {
        x: layout.playerStatus.x,
        y: layout.playerStatus.y + 29,
        width: statusWidth,
        height: 12,
      }, snapshot.player.hp, stats.maxHp, COLORS.hp);
      if (stats.maxMp > 0) {
        bar(context, {
          x: layout.playerStatus.x,
          y: layout.playerStatus.y + 45,
          width: statusWidth,
          height: 7,
        }, snapshot.player.mp, stats.maxMp, COLORS.mp);
      }
    }
    context.fillStyle = "rgba(9,7,10,.9)";
    context.fillRect(0, layout.logArea.y - 8, this.logicalWidth, this.logicalHeight - layout.logArea.y + 8);
    context.strokeStyle = "rgba(218,168,93,.4)";
    context.beginPath();
    context.moveTo(0, layout.logArea.y - 8);
    context.lineTo(this.logicalWidth, layout.logArea.y - 8);
    context.stroke();
    const attackButton = layout.actionButtons[0];
    const skillButton = layout.actionButtons[1];
    const itemButton = layout.actionButtons[2];
    const fleeButton = layout.actionButtons[3];
    if (attackButton) {
      this.button(context, "battle:attack", attackButton, "普通攻击", () => this.performBattleAction(), COLORS.gold);
    }
    if (skillButton) {
      const skillCount = this.engine.availableBattleSkills().length;
      this.button(context, "battle:skills", skillButton, skillCount > 0 ? `技能 (${skillCount})` : "技能", () => {
        this.battleMenu = this.battleMenu === "skills" ? null : "skills";
      });
    }
    if (itemButton) {
      const itemCount = this.engine.availableBattleItems()
        .reduce((sum, item) => sum + (snapshot.player.items[item.id] ?? 0), 0);
      this.button(context, "battle:items", itemButton, itemCount > 0 ? `物品 (${itemCount})` : "物品", () => {
        this.battleMenu = this.battleMenu === "items" ? null : "items";
      });
    }
    if (fleeButton) {
      this.button(context, "battle:flee", fleeButton, battle.canEscape ? "撤离" : "不可逃", () => this.engine.flee());
    }
    const autoSetting = layout.settingButtons[0];
    const speedSetting = layout.settingButtons[1];
    if (autoSetting) {
      this.settingToggle(context, "battle:auto", autoSetting, "自动", battle.auto, () => {
        this.battleMenu = null;
        this.engine.setAutoBattle(!battle.auto);
      });
    }
    if (speedSetting) {
      this.settingToggle(context, "battle:speed", speedSetting, `${this.battlePacer.speed()}×`, this.battlePacer.speed() === 2, () => {
        this.battlePacer.toggleSpeed();
      });
    }
    battle.log.slice(0, layout.mode === "portrait" ? 2 : 1).forEach((line, index) => {
      text(context, line, layout.logArea.x, layout.logArea.y + 17 + index * 19, { color: index === 0 ? COLORS.text : COLORS.muted, font: "500 12px sans-serif" });
    });
    if (this.battleMenu) this.drawBattleMenu(context, snapshot, this.battleMenu);
  }

  private settingToggle(
    context: CanvasRenderingContext2D,
    id: string,
    rect: Rect,
    label: string,
    active: boolean,
    run: () => void,
  ): void {
    panel(
      context,
      rect,
      active ? "rgba(37,82,59,.96)" : "rgba(31,25,33,.96)",
      active ? COLORS.success : "#5b4a51",
      9,
    );
    const markerSize = Math.min(14, rect.height - 12);
    const marker = {
      x: rect.x + 9,
      y: rect.y + (rect.height - markerSize) / 2,
      width: markerSize,
      height: markerSize,
    };
    roundedPath(context, marker, 3);
    context.fillStyle = active ? COLORS.success : "#4b4147";
    context.fill();
    if (active) {
      text(context, "✓", marker.x + marker.width / 2, marker.y + marker.height - 2, {
        align: "center",
        color: "#fff",
        font: "700 11px sans-serif",
      });
    }
    text(context, label, rect.x + markerSize + 15, rect.y + rect.height / 2 + 5, {
      color: active ? "#e8fff1" : COLORS.muted,
      font: "700 12px sans-serif",
      maxWidth: rect.width - markerSize - 20,
    });
    this.addHit(id, rect, run);
  }

  private drawBattleMenu(
    context: CanvasRenderingContext2D,
    snapshot: V1GameSnapshot,
    menu: "skills" | "items",
  ): void {
    const portrait = this.logicalHeight > this.logicalWidth;
    const width = Math.min(portrait ? this.logicalWidth - 28 : 570, this.logicalWidth - 28);
    const entries = menu === "skills"
      ? this.engine.availableBattleSkills()
      : this.engine.availableBattleItems();
    const visibleCount = Math.max(1, Math.min(5, entries.length));
    const height = 82 + visibleCount * 66;
    const rect = {
      x: (this.logicalWidth - width) / 2,
      y: Math.max(66, (this.logicalHeight - height) / 2 - 12),
      width,
      height,
    };
    context.fillStyle = "rgba(4,3,6,.62)";
    context.fillRect(0, 0, this.logicalWidth, this.logicalHeight);
    this.addHit("battle-menu:backdrop", {
      x: 0,
      y: 0,
      width: this.logicalWidth,
      height: this.logicalHeight,
    }, () => {
      this.battleMenu = null;
    });
    panel(context, rect, "rgba(23,17,25,.995)", COLORS.gold, 16);
    text(context, menu === "skills" ? "选择技能" : "选择战斗物品", rect.x + 22, rect.y + 39, {
      color: COLORS.gold,
      font: "700 21px 'Songti SC',serif",
    });
    this.button(context, "battle-menu:close", {
      x: rect.x + rect.width - 48,
      y: rect.y + 12,
      width: 32,
      height: 30,
    }, "×", () => {
      this.battleMenu = null;
    });
    if (entries.length === 0) {
      text(
        context,
        menu === "skills" ? "当前没有可用的主动技能" : "背包中没有可在战斗使用的物品",
        rect.x + rect.width / 2,
        rect.y + 94,
        { align: "center", color: COLORS.muted, font: "600 14px sans-serif" },
      );
      return;
    }
    entries.slice(0, 5).forEach((entry, index) => {
      const row = {
        x: rect.x + 18,
        y: rect.y + 57 + index * 66,
        width: rect.width - 36,
        height: 56,
      };
      if (menu === "skills") {
        const skill = entry as ReturnType<V1GameEngine["availableBattleSkills"]>[number];
        const canUse = this.engine.canUseBattleSkill(skill.id);
        panel(context, row, COLORS.panelSoft, canUse ? "#6f573e" : "#40383e", 8);
        drawAtlas(context, this.atlases.skills[skill.iconSheet - 1], skill.iconIndex, {
          x: row.x + 7,
          y: row.y + 6,
          width: 44,
          height: 44,
        });
        text(context, skill.name, row.x + 62, row.y + 23, {
          color: canUse ? COLORS.text : COLORS.muted,
          font: "700 14px sans-serif",
        });
        const cost = this.engine.battleSkillCost(skill.id);
        text(context, `${skill.description || "主动技能"}${cost > 0 ? ` · MP ${cost}` : ""}`, row.x + 62, row.y + 43, {
          color: COLORS.muted,
          font: "500 11px sans-serif",
          maxWidth: row.width - 76,
        });
        if (canUse) {
          this.addHit(`battle-menu:skill:${skill.id}`, row, () => {
            if (this.engine.performBattleSkill(skill.id)) {
              this.battleActionCooldownMs = 1_150;
              this.battleMenu = null;
            }
          });
        }
      } else {
        const item = entry as V1Item;
        const count = snapshot.player.items[item.id] ?? 0;
        const canUse = this.engine.canUseBattleItem(item.id);
        panel(context, row, COLORS.panelSoft, canUse ? "#456d55" : "#40383e", 8);
        drawAtlas(context, this.atlases.items, item.iconIndex, {
          x: row.x + 7,
          y: row.y + 6,
          width: 44,
          height: 44,
        }, 8, 8);
        text(context, `${item.name} ×${count}`, row.x + 62, row.y + 23, {
          color: canUse ? COLORS.text : COLORS.muted,
          font: "700 14px sans-serif",
        });
        text(context, item.description, row.x + 62, row.y + 43, {
          color: COLORS.muted,
          font: "500 11px sans-serif",
          maxWidth: row.width - 76,
        });
        if (canUse) {
          this.addHit(`battle-menu:item:${item.id}`, row, () => {
            if (this.engine.useBattleItem(item.id)) {
              this.battleActionCooldownMs = 1_150;
              this.battleMenu = null;
            }
          });
        }
      }
    });
  }

  private drawSkillAnimation(
    context: CanvasRenderingContext2D,
    layout: RpgBattleLayout,
    snapshot: V1GameSnapshot,
  ): void {
    const battle = snapshot.battle;
    const frame = this.skillAnimation.frame();
    if (!battle || !frame?.active) return;
    const unitIndex = battle.enemies.findIndex((entry) => entry.index === frame.targetIndex);
    const target = frame.targetIndex < 0
      ? battlePlayerRect(layout)
      : computeRpgBattleEnemySlots(layout, battle.enemies.length)[unitIndex];
    if (!target) return;
    const group = Math.abs(frame.animationId - 1) % 8;
    const atlas = this.atlases.combatEffects[Math.floor(group / 4)];
    const row = group % 4;
    const baseSize = Math.min(target.width, target.height) * 0.82;
    const size = baseSize * frame.effectScale;
    context.save();
    context.globalAlpha = frame.alpha;
    const centerX = target.x + target.width / 2;
    const centerY = target.y + target.height * 0.42;
    context.globalCompositeOperation = "screen";
    drawAtlas(
      context,
      atlas,
      row * 4 + frame.atlasFrame,
      {
        x: target.x + target.width / 2 - size / 2,
        y: target.y + target.height * 0.42 - size / 2,
        width: size,
        height: size,
      },
      4,
      4,
    );
    context.strokeStyle = "#ffd47d";
    context.lineWidth = 5;
    context.shadowColor = "#ff8a45";
    context.shadowBlur = 18;
    context.beginPath();
    context.arc(
      centerX,
      centerY,
      size * 0.34,
      -Math.PI * 0.2,
      Math.PI * 1.45,
    );
    context.stroke();
    context.lineWidth = 7;
    context.beginPath();
    context.moveTo(centerX - size * 0.38, centerY + size * 0.28);
    context.lineTo(centerX + size * 0.38, centerY - size * 0.28);
    context.stroke();
    context.restore();
    if (frame.phase === "impact" || frame.phase === "release") {
      text(
        context,
        `${frame.kind === "healing" ? "+" : "-"}${frame.amount}`,
        target.x + target.width / 2,
        target.y + 42 + frame.numberOffsetY,
        {
          align: "center",
          color: frame.kind === "healing" ? COLORS.success : "#ffd5b3",
          font: "800 22px sans-serif",
        },
      );
    }
  }

  private modalRect(width = 720, height = 560): Rect {
    const portrait = this.logicalHeight > this.logicalWidth;
    return portrait
      ? { x: 14, y: 48, width: this.logicalWidth - 28, height: Math.min(height, this.logicalHeight - 96) }
      : { x: this.logicalWidth / 2 - width / 2, y: this.logicalHeight / 2 - height / 2, width, height };
  }

  private drawInventory(context: CanvasRenderingContext2D, snapshot: V1GameSnapshot): void {
    const layout = computeRpgCharacterWindowLayout(
      this.logicalWidth,
      this.logicalHeight,
    );
    context.fillStyle = "rgba(5,4,6,.82)";
    context.fillRect(0, 0, this.logicalWidth, this.logicalHeight);
    panel(context, layout.panel, "rgba(20,15,21,.99)", COLORS.gold, 16);
    text(context, "角色与背包", layout.panel.x + 22, layout.panel.y + 38, {
      color: COLORS.text,
      font: `600 ${layout.panel.width < 600 ? 22 : 26}px 'Songti SC',serif`,
    });
    this.button(context, "inventory:close", layout.closeButton, "×", () => {
      this.selectedEquipmentInstanceId = null;
      this.selectedEquipmentAnchor = null;
      this.selectedItemId = null;
      this.selectedItemAnchor = null;
      this.bulkSaleOpen = false;
      this.equipmentSkillsOpen = false;
      this.engine.closePanel();
    });
    const equipmentAbilities = this.engine.equipmentBossAbilities();
    this.button(context, "inventory:equipment-skills", {
      x: layout.closeButton.x - 132,
      y: layout.closeButton.y + 3,
      width: 122,
      height: layout.closeButton.height - 6,
    }, `装备能力 ${equipmentAbilities.length}`, () => {
      this.selectedEquipmentInstanceId = null;
      this.selectedEquipmentAnchor = null;
      this.selectedItemId = null;
      this.selectedItemAnchor = null;
      this.bulkSaleOpen = false;
      this.equipmentSkillsOpen = true;
      this.selectedEquipmentSkillId = equipmentAbilities[0]?.id ?? null;
      this.equipmentSkillPage = 0;
    });
    this.drawCharacterStats(context, snapshot, layout.stats);
    this.drawEquippedGrid(context, snapshot, layout.equipment);
    this.drawBackpackGrid(context, snapshot, layout.inventory);
    if (this.equipmentSkillsOpen) {
      this.drawEquipmentSkills(context);
    } else if (this.bulkSaleOpen) {
      this.drawBulkSale(context, snapshot);
    } else if (this.selectedEquipmentInstanceId && this.selectedEquipmentAnchor) {
      this.drawEquipmentDetail(
        context,
        snapshot,
        this.selectedEquipmentInstanceId,
        this.selectedEquipmentAnchor,
      );
    } else if (this.selectedItemId && this.selectedItemAnchor) {
      this.drawItemDetail(
        context,
        snapshot,
        this.selectedItemId,
        this.selectedItemAnchor,
      );
    }
  }

  private drawEquipmentSkills(context: CanvasRenderingContext2D): void {
    const abilities = this.engine.equipmentBossAbilities();
    const rect = this.modalRect(720, 560);
    context.fillStyle = "rgba(4,3,5,.72)";
    context.fillRect(0, 0, this.logicalWidth, this.logicalHeight);
    panel(context, rect, "rgba(20,15,21,.995)", COLORS.gold, 16);
    text(context, "当前装备能力", rect.x + 24, rect.y + 38, {
      color: COLORS.gold,
      font: "700 22px 'Songti SC',serif",
    });
    text(context, "Boss 专属触发追击与常驻光环，卸下来源装备后立即失效", rect.x + 178, rect.y + 37, {
      color: COLORS.muted,
      font: "500 11px sans-serif",
      maxWidth: rect.width - 250,
    });
    this.button(context, "equipment-skills:close", {
      x: rect.x + rect.width - 52,
      y: rect.y + 10,
      width: 36,
      height: 32,
    }, "×", () => {
      this.equipmentSkillsOpen = false;
    });
    if (abilities.length === 0) {
      text(context, "当前没有 Boss 专属装备能力。击败 Boss 获得专属装备后，穿戴即可激活。", rect.x + rect.width / 2, rect.y + 112, {
        align: "center",
        color: COLORS.muted,
        font: "600 14px sans-serif",
        maxWidth: rect.width - 48,
      });
      return;
    }
    if (!abilities.some((ability) => ability.id === this.selectedEquipmentSkillId)) {
      this.selectedEquipmentSkillId = abilities[0]?.id ?? null;
    }
    const pageSize = 4;
    const pageCount = Math.max(1, Math.ceil(abilities.length / pageSize));
    this.equipmentSkillPage = Math.max(0, Math.min(this.equipmentSkillPage, pageCount - 1));
    const visible = abilities.slice(
      this.equipmentSkillPage * pageSize,
      (this.equipmentSkillPage + 1) * pageSize,
    );
    const gap = 8;
    const cardWidth = (rect.width - 48 - gap) / 2;
    visible.forEach((ability, index) => {
      const card = {
        x: rect.x + 24 + (index % 2) * (cardWidth + gap),
        y: rect.y + 58 + Math.floor(index / 2) * 55,
        width: cardWidth,
        height: 47,
      };
      const selected = ability.id === this.selectedEquipmentSkillId;
      panel(context, card, selected ? "rgba(75,52,36,.98)" : COLORS.panelSoft, selected ? COLORS.gold : "#5b493e", 8);
      panel(context, {
        x: card.x + 6,
        y: card.y + 6,
        width: 35,
        height: 35,
      }, ability.procChancePercent > 0 ? "rgba(113,36,43,.96)" : "rgba(51,57,112,.96)", selected ? COLORS.gold : "#6a5050", 8);
      text(context, ability.procChancePercent > 0 ? "击" : "环", card.x + 23.5, card.y + 30, {
        align: "center",
        color: COLORS.text,
        font: "800 16px serif",
      });
      text(context, ability.name, card.x + 50, card.y + 20, {
        color: selected ? COLORS.gold : COLORS.text,
        font: "700 13px sans-serif",
        maxWidth: card.width - 56,
      });
      text(context, ability.procChancePercent > 0
        ? `${ability.procChancePercent}%触发 · 追加${ability.procDamagePercent}%伤害`
        : `常驻光环 · 主动技能伤害+${ability.skillDamageBonusPercent}%`, card.x + 50, card.y + 38, {
        color: COLORS.muted,
        font: "500 10px sans-serif",
        maxWidth: card.width - 56,
      });
      this.addHit(`equipment-skills:select:${ability.id}`, card, () => {
        this.selectedEquipmentSkillId = ability.id;
      });
    });
    if (pageCount > 1) {
      this.button(context, "equipment-skills:previous", {
        x: rect.x + rect.width - 142,
        y: rect.y + 166,
        width: 48,
        height: 26,
      }, "‹", () => {
        this.equipmentSkillPage = (this.equipmentSkillPage + pageCount - 1) % pageCount;
        this.selectedEquipmentSkillId = abilities[this.equipmentSkillPage * pageSize]?.id ?? null;
      });
      text(context, `${this.equipmentSkillPage + 1}/${pageCount}`, rect.x + rect.width - 82, rect.y + 185, {
        align: "center",
        color: COLORS.muted,
        font: "600 11px sans-serif",
      });
      this.button(context, "equipment-skills:next", {
        x: rect.x + rect.width - 62,
        y: rect.y + 166,
        width: 48,
        height: 26,
      }, "›", () => {
        this.equipmentSkillPage = (this.equipmentSkillPage + 1) % pageCount;
        this.selectedEquipmentSkillId = abilities[this.equipmentSkillPage * pageSize]?.id ?? null;
      });
    }
    const selected = abilities.find((ability) => ability.id === this.selectedEquipmentSkillId);
    if (!selected) return;
    const detail = {
      x: rect.x + 24,
      y: rect.y + 202,
      width: rect.width - 48,
      height: rect.height - 222,
    };
    panel(context, detail, "rgba(31,24,32,.98)", "#7e5b3b", 11);
    panel(context, {
      x: detail.x + 14,
      y: detail.y + 14,
      width: 58,
      height: 58,
    }, selected.procChancePercent > 0 ? "rgba(113,36,43,.96)" : "rgba(51,57,112,.96)", COLORS.gold, 12);
    text(context, selected.procChancePercent > 0 ? "追击" : "光环", detail.x + 43, detail.y + 50, {
      align: "center",
      color: COLORS.text,
      font: "800 15px serif",
    });
    text(context, selected.name, detail.x + 86, detail.y + 36, {
      color: COLORS.gold,
      font: "700 21px serif",
      maxWidth: detail.width - 104,
    });
    const bossName = selected.bossEnemyId === null
      ? "Boss 专属能力"
      : `${this.engine.content.indexes.enemies.get(selected.bossEnemyId)?.name ?? `Boss ${selected.bossEnemyId}`}专属`;
    text(context, selected.procChancePercent > 0 ? `${bossName} · 概率触发` : `${bossName} · 常驻生效`, detail.x + 86, detail.y + 59, {
      color: COLORS.muted,
      font: "600 12px sans-serif",
      maxWidth: detail.width - 104,
    });
    context.font = "600 14px 'PingFang SC','Microsoft YaHei',sans-serif";
    context.fillStyle = COLORS.text;
    const mechanicLines = selected.procChancePercent > 0
      ? [
          `触发条件：普通攻击或主动技能每次造成伤害时独立判定。`,
          `触发概率：${selected.procChancePercent}%`,
          `追击伤害：追加本次伤害的 ${selected.procDamagePercent}%`,
          `叠加规则：不同追击可以同时触发，同名能力只生效一次。`,
        ]
      : [
          `生效条件：穿戴后常驻，无需释放。`,
          `光环效果：所有主动技能伤害提高 ${selected.skillDamageBonusPercent}%`,
          `普通攻击：不受该光环加成。`,
          `叠加规则：不同光环相加，总技能增伤最多 150%。`,
        ];
    wrap(context, selected.description, detail.x + 16, detail.y + 98, detail.width - 32, 19, 2);
    mechanicLines.forEach((line, index) => {
      text(context, line, detail.x + 16, detail.y + 152 + index * 31, {
        color: index === 1 || index === 2 ? COLORS.gold : COLORS.text,
        font: index === 1 || index === 2 ? "700 14px sans-serif" : "500 13px sans-serif",
        maxWidth: detail.width - 32,
      });
    });
  }

  private drawCharacterStats(
    context: CanvasRenderingContext2D,
    snapshot: V1GameSnapshot,
    rect: Rect,
  ): void {
    panel(context, rect, "rgba(33,25,31,.84)", "#4f3d35", 10);
    const level = this.engine.content.indexes.levels.get(snapshot.player.level);
    const next = this.engine.content.indexes.levels.get(snapshot.player.level + 1);
    const experienceItems = [
      `Lv.${snapshot.player.level}`,
      `当前经验 ${snapshot.player.exp.toLocaleString("zh-CN")}`,
      `下一级经验 ${(next?.expRequired ?? level?.expRequired ?? 0).toLocaleString("zh-CN")}`,
    ];
    const experienceWidth = rect.width / experienceItems.length;
    experienceItems.forEach((value, index) => {
      text(context, value, rect.x + experienceWidth * (index + 0.5), rect.y + 20, {
        align: "center",
        color: index === 0 ? COLORS.gold : COLORS.text,
        font: `${index === 0 ? "700 13px" : "600 11px"} 'PingFang SC',sans-serif`,
        maxWidth: experienceWidth - 10,
      });
    });
    bar(
      context,
      { x: rect.x + 12, y: rect.y + 29, width: rect.width - 24, height: 7 },
      snapshot.player.exp,
      Math.max(1, next?.expRequired ?? level?.expRequired ?? 1),
      COLORS.gold,
    );
    const stats = this.engine.playerStats();
    const entries: Array<[string, number]> = [
      ["攻击", stats.attack],
      ["魔攻", stats.magicAttack],
      ["防御", stats.defense],
      ["魔防", stats.magicDefense],
      ["敏捷", stats.agility],
      ["幸运", stats.luck],
    ];
    const itemWidth = rect.width / entries.length;
    entries.forEach(([label, value], index) => {
      const centerX = rect.x + itemWidth * (index + 0.5);
      text(context, label, centerX, rect.y + 62, {
        align: "center",
        color: COLORS.muted,
        font: "500 11px sans-serif",
      });
      text(context, String(value), centerX, rect.y + 89, {
        align: "center",
        color: COLORS.text,
        font: "700 17px sans-serif",
      });
    });
  }

  private drawEquippedGrid(
    context: CanvasRenderingContext2D,
    snapshot: V1GameSnapshot,
    rect: Rect,
  ): void {
    const gap = this.logicalHeight > this.logicalWidth ? 5 : 8;
    const cellSize = Math.min(
      (rect.width - gap * 4) / 5,
      (rect.height - gap * 4) / 5,
    );
    const layoutWidth = cellSize * 5 + gap * 4;
    const layoutHeight = cellSize * 5 + gap * 4;
    const originX = rect.x + (rect.width - layoutWidth) / 2;
    const originY = rect.y + (rect.height - layoutHeight) / 2;
    const centerX = originX + layoutWidth / 2;
    context.save();
    context.strokeStyle = "rgba(220,185,126,.14)";
    context.fillStyle = "rgba(220,185,126,.035)";
    context.lineWidth = Math.max(5, cellSize * 0.15);
    context.lineCap = "round";
    context.beginPath();
    context.arc(centerX, originY + cellSize * 0.6, cellSize * 0.28, 0, Math.PI * 2);
    context.fill();
    context.stroke();
    context.beginPath();
    context.moveTo(centerX, originY + cellSize * 1.05);
    context.lineTo(centerX, originY + cellSize * 3.72);
    context.moveTo(centerX, originY + cellSize * 1.55);
    context.lineTo(centerX - cellSize * 1.15, originY + cellSize * 2.55);
    context.moveTo(centerX, originY + cellSize * 1.55);
    context.lineTo(centerX + cellSize * 1.15, originY + cellSize * 2.55);
    context.moveTo(centerX, originY + cellSize * 3.55);
    context.lineTo(centerX - cellSize * 0.62, originY + cellSize * 4.65);
    context.moveTo(centerX, originY + cellSize * 3.55);
    context.lineTo(centerX + cellSize * 0.62, originY + cellSize * 4.65);
    context.stroke();
    context.restore();
    for (const slot of V1_EQUIPMENT_SLOTS) {
      const cell: Rect = {
        x: originX + slot.column * (cellSize + gap),
        y: originY + slot.row * (cellSize + gap),
        width: cellSize,
        height: cellSize,
      };
      const instance = snapshot.player.equipment[slot.slotId];
      const item = instance ? this.engine.deriveEquipment(instance) : undefined;
      panel(
        context,
        cell,
        item ? "rgba(39,29,35,.94)" : "rgba(24,20,25,.82)",
        item ? QUALITY_COLORS[item.qualityId - 1] ?? COLORS.border : "#473a3d",
        9,
      );
      const iconSize = Math.max(18, Math.min(cell.width - 10, cell.height - 20));
      context.globalAlpha = item ? 1 : 0.22;
      drawAtlas(
        context,
        this.atlases.equipment,
        slot.atlasIndex,
        {
          x: cell.x + (cell.width - iconSize) / 2,
          y: cell.y + 3,
          width: iconSize,
          height: iconSize,
        },
      );
      context.globalAlpha = 1;
      if (item) {
        this.drawExceptionalBadge(
          context,
          item,
          cell,
          this.logicalHeight > this.logicalWidth,
        );
      }
      const slotName =
        this.engine.content.dictionary.get("equipment_slot")?.get(slot.slotId) ??
        slot.fallbackName;
      text(context, slotName, cell.x + cell.width / 2, cell.y + cell.height - 8, {
        align: "center",
        color: item ? COLORS.text : "#766d6f",
        font: `600 ${this.logicalHeight > this.logicalWidth ? 9 : 10}px sans-serif`,
        maxWidth: cell.width - 4,
      });
      if (item) {
        this.addHit(`equipment:${slot.slotId}`, cell, () => {
          this.selectedEquipmentInstanceId = item.instanceId;
          this.selectedEquipmentAnchor = cell;
          this.selectedItemId = null;
          this.selectedItemAnchor = null;
        });
      }
    }
  }

  private drawBackpackGrid(
    context: CanvasRenderingContext2D,
    snapshot: V1GameSnapshot,
    rect: Rect,
  ): void {
    const entries = [
      ...this.engine.equipmentInventory().map((item) => ({
        kind: "equipment" as const,
        item,
      })),
      ...Object.entries(snapshot.player.items)
        .filter(([, count]) => count > 0)
        .map(([itemId, count]) => ({
          kind: "item" as const,
          itemId: Number(itemId),
          count,
        })),
    ];
    text(context, `背包 ${entries.length} · 64格/页`, rect.x, rect.y + 20, {
      color: COLORS.gold,
      font: "600 15px sans-serif",
    });
    this.button(context, "inventory:bulk-sale", {
      x: rect.x + rect.width - 102,
      y: rect.y - 2,
      width: 102,
      height: 28,
    }, "批量出售", () => {
      this.selectedEquipmentInstanceId = null;
      this.selectedEquipmentAnchor = null;
      this.selectedItemId = null;
      this.selectedItemAnchor = null;
      this.bulkSaleOpen = true;
    });
    const gridLayout = computeRpgInventoryGridLayout(rect, {
      columns: 8,
      rows: 8,
      portrait: this.logicalHeight > this.logicalWidth,
      headerHeight: 34,
      footerHeight: 32,
    });
    const pageSize = gridLayout.pageSize;
    const pageCount = Math.max(1, Math.ceil(entries.length / pageSize));
    this.inventoryPage = Math.max(0, Math.min(this.inventoryPage, pageCount - 1));
    const visible = entries.slice(
      this.inventoryPage * pageSize,
      (this.inventoryPage + 1) * pageSize,
    );
    for (let index = 0; index < pageSize; index += 1) {
      const cell = gridLayout.cells[index];
      if (!cell) continue;
      const entry = visible[index];
      const equipment = entry?.kind === "equipment" ? entry.item : undefined;
      panel(
        context,
        cell,
        entry ? "rgba(38,28,35,.94)" : "rgba(23,19,24,.62)",
        equipment
          ? QUALITY_COLORS[equipment.qualityId - 1] ?? COLORS.border
          : entry
            ? COLORS.border
            : "#3c3337",
        Math.min(7, cell.width * 0.18),
      );
      if (!entry) continue;
      const iconSize = Math.max(8, Math.min(cell.width - 5, cell.height - 5));
      const iconRect = {
        x: cell.x + (cell.width - iconSize) / 2,
        y: cell.y + (cell.height - iconSize) / 2,
        width: iconSize,
        height: iconSize,
      };
      if (entry.kind === "equipment") {
        const base = this.engine.content.indexes.equipment.get(entry.item.equipmentId);
        drawAtlas(
          context,
          this.atlases.equipment,
          v1PrimaryEquipmentSlot(base?.equipmentType ?? 1)?.atlasIndex ?? 0,
          iconRect,
        );
        this.drawExceptionalBadge(
          context,
          entry.item,
          cell,
          true,
        );
        this.addHit(`inventory:${entry.item.instanceId}`, cell, () => {
          this.selectedEquipmentInstanceId = entry.item.instanceId;
          this.selectedEquipmentAnchor = cell;
          this.selectedItemId = null;
          this.selectedItemAnchor = null;
        });
      } else {
        const item = this.engine.content.indexes.items.get(entry.itemId);
        drawAtlas(context, this.atlases.items, item?.iconIndex ?? 0, iconRect, 8, 8);
        text(context, entry.count > 99 ? "99+" : String(entry.count), cell.x + cell.width - 3, cell.y + cell.height - 3, {
          align: "right",
          color: COLORS.text,
          font: "700 9px sans-serif",
        });
        this.addHit(`inventory:item:${entry.itemId}`, cell, () => {
          this.selectedItemId = entry.itemId;
          this.selectedItemAnchor = cell;
          this.selectedEquipmentInstanceId = null;
          this.selectedEquipmentAnchor = null;
        });
      }
    }
    if (pageCount > 1) {
      text(context, `${this.inventoryPage + 1}/${pageCount}`, gridLayout.pager.x + gridLayout.pager.width / 2, gridLayout.pager.y + 24, {
        align: "center",
        color: COLORS.muted,
        font: "500 12px sans-serif",
      });
      this.button(context, "inventory:prev", { x: gridLayout.pager.x, y: gridLayout.pager.y + 1, width: 72, height: 28 }, "上一页", () => {
        this.inventoryPage = Math.max(0, this.inventoryPage - 1);
      });
      this.button(context, "inventory:next", { x: gridLayout.pager.x + gridLayout.pager.width - 72, y: gridLayout.pager.y + 1, width: 72, height: 28 }, "下一页", () => {
        this.inventoryPage = Math.min(pageCount - 1, this.inventoryPage + 1);
      });
    }
  }

  private drawEquipmentDetail(
    context: CanvasRenderingContext2D,
    snapshot: V1GameSnapshot,
    instanceId: string,
    anchor: Rect,
  ): void {
    const inventory = snapshot.player.inventory.find(
      (entry) => entry.instanceId === instanceId,
    );
    const equipped = Object.entries(snapshot.player.equipment).find(
      ([, entry]) => entry.instanceId === instanceId,
    );
    const instance = inventory ?? equipped?.[1];
    if (!instance) {
      this.selectedEquipmentInstanceId = null;
      this.selectedEquipmentAnchor = null;
      return;
    }
    const item = this.engine.deriveEquipment(instance);
    const base = this.engine.content.indexes.equipment.get(item.equipmentId);
    if (!base) return;
    const portrait = this.logicalHeight > this.logicalWidth;
    const width = portrait ? Math.min(310, this.logicalWidth - 32) : 360;
    const height = 450;
    let x = portrait
      ? anchor.x + anchor.width / 2 - width / 2
      : anchor.x + anchor.width + 12;
    let y = portrait ? anchor.y - height - 10 : anchor.y;
    x = Math.max(14, Math.min(this.logicalWidth - width - 14, x));
    if (y < 14) y = anchor.y + anchor.height + 10;
    y = Math.max(14, Math.min(this.logicalHeight - height - 14, y));
    const popup = { x, y, width, height };
    context.fillStyle = "rgba(4,3,5,.58)";
    context.fillRect(0, 0, this.logicalWidth, this.logicalHeight);
    panel(
      context,
      popup,
      "rgba(24,17,25,.99)",
      QUALITY_COLORS[item.qualityId - 1] ?? COLORS.border,
      16,
    );
    const equippedSlot = equipped ? v1EquipmentSlot(Number(equipped[0])) : undefined;
    drawAtlas(
      context,
      this.atlases.equipment,
      equippedSlot?.atlasIndex
        ?? v1PrimaryEquipmentSlot(base.equipmentType)?.atlasIndex
        ?? 0,
      {
      x: popup.x + 16,
      y: popup.y + 16,
      width: 66,
      height: 66,
      },
    );
    text(context, item.name, popup.x + 96, popup.y + 39, {
      color: QUALITY_COLORS[item.qualityId - 1] ?? COLORS.text,
      font: "700 19px sans-serif",
      maxWidth: popup.width - 145,
    });
    const slotName =
      this.engine.content.dictionary.get("equipment_type")?.get(base.equipmentType) ??
      `装备${base.equipmentType}`;
    const equipmentSet = this.engine.content.sets.find((set) =>
      set.memberEquipment.includes(base.id)
    );
    const equippedIds = new Set(
      Object.values(snapshot.player.equipment).map((entry) => entry.equipmentId),
    );
    const setSummary = equipmentSet
      ? ` · ${equipmentSet.name} ${equipmentSet.memberEquipment.filter((id) => equippedIds.has(id)).length}/${equipmentSet.memberEquipment.length}`
      : "";
    text(context, `${slotName} · 品质 ${item.qualityId} · 价值 ${item.finalPrice}${setSummary}`, popup.x + 96, popup.y + 67, {
      color: COLORS.muted,
      font: "500 12px sans-serif",
      maxWidth: popup.width - 112,
    });
    this.button(context, "equipment-detail:close", {
      x: popup.x + popup.width - 44,
      y: popup.y + 14,
      width: 30,
      height: 30,
    }, "×", () => {
      this.selectedEquipmentInstanceId = null;
      this.selectedEquipmentAnchor = null;
    });
    context.fillStyle = "rgba(136,103,66,.45)";
    context.fillRect(popup.x + 16, popup.y + 96, popup.width - 32, 1);
    const comparison = equipped ? null : this.engine.compareEquipment(item.instanceId);
    const comparisonSlot = comparison ? v1EquipmentSlot(comparison.targetSlotId) : undefined;
    const comparisonLabel = equipped
      ? "装备属性 · 当前穿戴"
      : comparison
        ? `属性对比 · ${comparisonSlot?.fallbackName ?? `槽位${comparison.targetSlotId}`}：${comparison.current?.name ?? "空槽"}`
        : "装备属性";
    text(context, comparisonLabel, popup.x + 18, popup.y + 113, {
      color: comparison ? COLORS.gold : COLORS.muted,
      font: "700 11px sans-serif",
      maxWidth: popup.width - 36,
    });
    const statCandidates: Array<[string, keyof V1DerivedEquipment["stats"], number]> = [
      ["生命", "maxHp", item.stats.maxHp],
      ["魔法", "maxMp", item.stats.maxMp],
      ["攻击", "attack", item.stats.attack],
      ["防御", "defense", item.stats.defense],
      ["魔攻", "magicAttack", item.stats.magicAttack],
      ["魔防", "magicDefense", item.stats.magicDefense],
      ["敏捷", "agility", item.stats.agility],
      ["幸运", "luck", item.stats.luck],
    ];
    const stats = statCandidates.filter(([, key, value]) => value !== 0 || (comparison?.deltas[key] ?? 0) !== 0);
    const columnWidth = (popup.width - 36) / 2;
    stats.slice(0, 8).forEach(([label, key, value], index) => {
      const columnX = popup.x + 18 + (index % 2) * columnWidth;
      const delta = comparison?.deltas[key] ?? 0;
      text(context, `${label} ${value > 0 ? "+" : ""}${value}`, columnX, popup.y + 135 + Math.floor(index / 2) * 20, {
        color: COLORS.text,
        font: "600 12px sans-serif",
      });
      if (comparison) {
        text(context, delta > 0 ? `↑${delta}` : delta < 0 ? `↓${Math.abs(delta)}` : "—", columnX + columnWidth - 10, popup.y + 135 + Math.floor(index / 2) * 20, {
          align: "right",
          color: delta > 0 ? COLORS.success : delta < 0 ? COLORS.danger : COLORS.muted,
          font: "700 11px sans-serif",
        });
      }
    });
    context.fillStyle = "rgba(136,103,66,.3)";
    context.fillRect(popup.x + 16, popup.y + 214, popup.width - 32, 1);
    text(context, "词缀效果", popup.x + 18, popup.y + 235, {
      color: item.affixRolls.length > 0 ? COLORS.gold : COLORS.muted,
      font: "700 12px sans-serif",
    });
    if (item.affixRolls.length === 0) {
      text(context, "无", popup.x + 90, popup.y + 235, {
        color: COLORS.muted,
        font: "500 12px sans-serif",
      });
    } else {
      item.affixRolls.slice(0, 4).forEach((roll, index) => {
        const affix = this.engine.content.indexes.affixes.get(roll.affixId);
        context.font = "500 11px 'PingFang SC','Microsoft YaHei',sans-serif";
        context.fillStyle = affix?.qualityId === null ? "#ff8fc5" : COLORS.text;
        wrap(
          context,
          this.affixEffectSummary(roll),
          popup.x + 18,
          popup.y + 258 + index * 31,
          popup.width - 36,
          14,
          2,
        );
      });
    }
    text(context, `穿戴等级 Lv.${base.requiredLevel}${snapshot.player.level >= base.requiredLevel ? " · 已满足" : ` · 当前 Lv.${snapshot.player.level}`}`, popup.x + 18, popup.y + 389, {
      color: snapshot.player.level >= base.requiredLevel ? COLORS.success : COLORS.danger,
      font: "700 12px sans-serif",
    });
    const action = {
      x: popup.x + 16,
      y: popup.y + popup.height - 48,
      width: popup.width - 32,
      height: 34,
    };
    if (equipped) {
      this.button(context, "equipment-detail:unequip", action, base.canRemove ? "卸下" : "不可卸下", () => {
        if (base.canRemove) this.engine.unequip(Number(equipped[0]));
        this.selectedEquipmentInstanceId = null;
        this.selectedEquipmentAnchor = null;
      });
    } else {
      const canEquip = snapshot.player.level >= base.requiredLevel;
      const gap = 8;
      const actionWidth = (action.width - gap) / 2;
      const equipAction = { ...action, width: actionWidth };
      const sellAction = {
        ...action,
        x: action.x + actionWidth + gap,
        width: actionWidth,
      };
      this.button(context, "equipment-detail:equip", equipAction, canEquip ? "装备" : `需要 Lv.${base.requiredLevel}`, () => {
        if (canEquip) this.engine.equip(item.instanceId);
        this.selectedEquipmentInstanceId = null;
        this.selectedEquipmentAnchor = null;
      });
      const salePrice = this.engine.equipmentSalePrice(item);
      this.button(context, "equipment-detail:sell", sellAction, base.canSell && salePrice > 0 ? `出售 +${salePrice}` : "不可出售", () => {
        if (base.canSell && salePrice > 0) {
          this.engine.sellEquipment(item.instanceId);
        }
        this.selectedEquipmentInstanceId = null;
        this.selectedEquipmentAnchor = null;
      });
    }
  }

  private affixEffectSummary(roll: V1AffixRoll): string {
    const affix = this.engine.content.indexes.affixes.get(roll.affixId);
    if (!affix) return `词缀 ${roll.affixId}：数据缺失`;
    const labels: Array<[keyof typeof affix, string]> = [
      ["maxHp", "生命"],
      ["maxMp", "魔法"],
      ["attack", "攻击"],
      ["defense", "防御"],
      ["magicAttack", "魔攻"],
      ["magicDefense", "魔防"],
      ["agility", "敏捷"],
      ["luck", "幸运"],
    ];
    const effects = labels
      .map(([key, label]) => [label, affix[key]] as const)
      .filter(([, value]) => typeof value === "number" && value !== 0)
      .map(([label, value]) => `${label} ${Number(value) > 0 ? "+" : ""}${value}`);
    if (roll.statAllocations && roll.statAllocations.length > 0) {
      for (const allocation of roll.statAllocations) {
        const stat = ["生命", "魔法", "攻击", "防御", "魔攻", "魔防", "敏捷", "幸运"][
          allocation.statCode
        ] ?? `属性${allocation.statCode}`;
        effects.push(`${stat} ${allocation.value > 0 ? "+" : ""}${allocation.value}`);
      }
    } else if (affix.randomStatCode !== null && roll.randomValue !== null) {
      const stat = ["生命", "魔法", "攻击", "防御", "魔攻", "魔防", "敏捷", "幸运"][
        affix.randomStatCode
      ] ?? `属性${affix.randomStatCode}`;
      effects.push(`${stat} ${roll.randomValue > 0 ? "+" : ""}${roll.randomValue}`);
    }
    effects.push(...affix.traits.map((trait) => this.traitEffectSummary(trait)));
    effects.push(...affix.passiveStates.map((stateId) => {
      const state = this.engine.content.states.find((entry) => entry.id === stateId);
      return `常驻状态：${state?.name ?? stateId}`;
    }));
    effects.push(...affix.lifeSteal.map((entry) => {
      const resource = entry.channel === 1 ? "吸血" : "吸蓝";
      return entry.mode === 1
        ? `${resource} ${entry.value}%`
        : `${resource} +${entry.value}/次`;
    }));
    if (affix.procChancePercent > 0) {
      effects.push(`Boss追击：${affix.procChancePercent}%概率追加${affix.procDamagePercent}%本次伤害`);
    }
    if (affix.skillDamageBonusPercent > 0) {
      effects.push(`Boss光环：主动技能伤害 +${affix.skillDamageBonusPercent}%`);
    }
    effects.push(...affix.grantedSkills.map((skillId) => {
      const skill = this.engine.content.indexes.skills.get(skillId);
      return `装备技能：${skill ? this.compactSkillSummary(skill) : skillId}`;
    }));
    const prefix = affix.qualityId === null ? "极品属性：" : "品质加点：";
    return `${prefix}${effects.join("、") || "仅提高装备价值"}`;
  }

  private traitEffectSummary(trait: V1Trait): string {
    const parameterNames = [
      "生命", "魔法", "攻击", "防御", "魔攻", "魔防", "敏捷", "幸运",
    ];
    if (trait.code === 21) {
      const label = parameterNames[trait.dataId] ?? `属性${trait.dataId}`;
      const percent = Math.round((trait.value - 1) * 10_000) / 100;
      return `${label} ${percent >= 0 ? "+" : ""}${percent}%`;
    }
    if (trait.code === 22) {
      const label = [
        "命中率", "闪避率", "暴击率", "暴击闪避", "暴击伤害",
        "魔法反射", "反击率", "生命再生", "魔法再生", "战意再生",
      ][trait.dataId] ?? `特殊属性${trait.dataId}`;
      const percent = Math.round(trait.value * 10_000) / 100;
      return `${label} ${percent >= 0 ? "+" : ""}${percent}%`;
    }
    if (trait.code === 23) {
      const label = [
        "受击率", "防御效果", "恢复效果", "药品效果", "魔法消耗",
        "战意获取", "物理承伤", "魔法承伤", "地形承伤", "经验获取",
      ][trait.dataId] ?? `倍率${trait.dataId}`;
      const percent = Math.round((trait.value - 1) * 10_000) / 100;
      return `${label} ${percent >= 0 ? "+" : ""}${percent}%`;
    }
    if (trait.code === 33) return `攻击速度 ${trait.value >= 0 ? "+" : ""}${trait.value}`;
    return `特性 ${trait.code}/${trait.dataId} = ${trait.value}`;
  }

  private drawItemDetail(
    context: CanvasRenderingContext2D,
    snapshot: V1GameSnapshot,
    itemId: number,
    _anchor: Rect,
  ): void {
    const item = this.engine.content.indexes.items.get(itemId);
    const count = snapshot.player.items[itemId] ?? 0;
    if (!item || count <= 0) {
      this.selectedItemId = null;
      this.selectedItemAnchor = null;
      return;
    }
    const portrait = this.logicalHeight > this.logicalWidth;
    const width = portrait ? Math.min(342, this.logicalWidth - 28) : 390;
    const height = 340;
    const popup = {
      x: (this.logicalWidth - width) / 2,
      y: (this.logicalHeight - height) / 2,
      width,
      height,
    };
    const effectLines = item.effects.length > 0
      ? item.effects.map((effect) => this.itemEffectSummary(item, effect.code, effect.params))
      : ["无可主动触发的效果"];
    context.fillStyle = "rgba(4,3,5,.58)";
    context.fillRect(0, 0, this.logicalWidth, this.logicalHeight);
    panel(context, popup, "rgba(24,17,25,.99)", COLORS.gold, 16);
    context.save();
    roundedPath(context, {
      x: popup.x + 2,
      y: popup.y + 2,
      width: popup.width - 4,
      height: popup.height - 4,
    }, 14);
    context.clip();
    drawAtlas(context, this.atlases.items, item.iconIndex, {
      x: popup.x + 16,
      y: popup.y + 16,
      width: 66,
      height: 66,
    }, 8, 8);
    text(context, item.name, popup.x + 96, popup.y + 39, {
      color: COLORS.gold,
      font: "700 19px sans-serif",
      maxWidth: popup.width - 145,
    });
    text(context, `持有 ${count} · ${this.itemTypeName(item)} · 价值 ${item.buyPrice}`, popup.x + 96, popup.y + 67, {
      color: COLORS.muted,
      font: "500 12px sans-serif",
      maxWidth: popup.width - 112,
    });
    this.button(context, "item-detail:close", {
      x: popup.x + popup.width - 44,
      y: popup.y + 14,
      width: 30,
      height: 30,
    }, "×", () => {
      this.selectedItemId = null;
      this.selectedItemAnchor = null;
    });
    context.fillStyle = "rgba(136,103,66,.45)";
    context.fillRect(popup.x + 16, popup.y + 96, popup.width - 32, 1);
    context.font = "500 13px 'PingFang SC',sans-serif";
    context.fillStyle = COLORS.text;
    wrap(context, item.description, popup.x + 18, popup.y + 121, popup.width - 36, 20, 3);
    text(context, "实际效果", popup.x + 18, popup.y + 188, {
      color: COLORS.gold,
      font: "700 12px sans-serif",
    });
    effectLines.slice(0, 4).forEach((line, index) => {
      text(context, line, popup.x + 18, popup.y + 212 + index * 20, {
        color: COLORS.text,
        font: "500 12px sans-serif",
        maxWidth: popup.width - 36,
      });
    });
    const supported = (item.usePlace === 1 || item.usePlace === 3) && item.effects.some(
      (effect) => effect.code === 11 || effect.code === 12 || effect.code === 43 || effect.code === 51,
    );
    const canUse = supported && this.engine.canUseItem(item.id);
    const learnEffects = item.effects.filter((effect) => effect.code === 43);
    const learnsSkill = learnEffects.length > 0;
    const alreadyLearned = learnsSkill && learnEffects
      .every((effect) => snapshot.player.learnedSkills.includes(effect.params[0] ?? -1));
    const useLabel = canUse
      ? learnsSkill ? "学习技能" : `使用 ${item.name}`
      : supported
        ? snapshot.player.level < item.requiredLevel
          ? `需要 Lv.${item.requiredLevel}`
          : alreadyLearned
            ? "技能已学会"
            : "当前状态无需使用"
        : "不可在此使用";
    this.button(context, "item-detail:use", {
      x: popup.x + 16,
      y: popup.y + popup.height - 48,
      width: popup.width - 32,
      height: 34,
    }, useLabel, () => {
      if (canUse) this.engine.useItem(item.id);
      if ((this.engine.snapshot.player.items[item.id] ?? 0) <= 0) {
        this.selectedItemId = null;
        this.selectedItemAnchor = null;
      }
    }, canUse ? COLORS.gold : "#4b3d40");
    context.restore();
  }

  private itemTypeName(item: V1Item): string {
    return this.engine.content.dictionary.get("item_type")?.get(item.type)
      ?? (item.type === 1 ? "消耗品" : item.type === 2 ? "技能书" : "重要物品");
  }

  private itemEffectSummary(
    _item: V1Item,
    code: number,
    params: readonly number[],
  ): string {
    if (code === 11 || code === 12) {
      const resource = code === 11 ? "生命" : "魔法";
      const percent = params[0] ?? 0;
      const flat = params[1] ?? 0;
      const terms = [
        percent !== 0 ? `${percent}%` : "",
        flat !== 0 ? `${flat > 0 ? "+" : ""}${flat}` : "",
      ].filter(Boolean);
      return `恢复${resource} ${terms.join(" ") || "0"}`;
    }
    if (code === 43) {
      const skill = this.engine.content.indexes.skills.get(params[0] ?? -1);
      return `习得技能：${skill?.name ?? params[0] ?? "未知"}`;
    }
    if (code === 44) {
      const skill = this.engine.content.indexes.skills.get(params[0] ?? -1);
      return `遗忘技能：${skill?.name ?? params[0] ?? "未知"}`;
    }
    if (code === 51) {
      const map = this.engine.content.indexes.maps.get(params[0] ?? -1);
      return `传送至：${map?.name ?? `地图${params[0] ?? "?"}`}`;
    }
    return `效果 ${code}：${params.join(" / ") || "-"}`;
  }

  private drawBulkSale(
    context: CanvasRenderingContext2D,
    snapshot: V1GameSnapshot,
  ): void {
    const width = this.logicalHeight > this.logicalWidth
      ? this.logicalWidth - 28
      : 560;
    const height = 276;
    const rect: Rect = {
      x: (this.logicalWidth - width) / 2,
      y: (this.logicalHeight - height) / 2,
      width,
      height,
    };
    const entries = this.engine.equipmentInventory().map((item) => {
      const base = this.engine.content.indexes.equipment.get(item.equipmentId);
      return {
        id: item.instanceId,
        qualityId: item.qualityId,
        salePrice: base?.canSell
          ? this.engine.equipmentSalePrice(item)
          : 0,
        locked: item.locked,
        equipped: false,
        exceptional: this.engine.isExceptionalEquipment(item),
      };
    });
    const quote = quoteRpgBulkSale(entries, {
      qualityIds: this.bulkSaleQualityIds,
    });
    context.fillStyle = "rgba(4,3,5,.72)";
    context.fillRect(0, 0, this.logicalWidth, this.logicalHeight);
    panel(context, rect, "rgba(24,17,25,.995)", COLORS.gold, 16);
    text(context, "按品质批量出售", rect.x + 20, rect.y + 36, {
      color: COLORS.gold,
      font: "700 21px 'Songti SC',serif",
    });
    this.button(context, "bulk-sale:close", {
      x: rect.x + rect.width - 48,
      y: rect.y + 12,
      width: 34,
      height: 30,
    }, "×", () => {
      this.bulkSaleOpen = false;
    });
    text(context, "选择要出售的品质", rect.x + 20, rect.y + 72, {
      color: COLORS.muted,
      font: "600 12px sans-serif",
    });
    const gap = 7;
    const buttonWidth = (rect.width - 40 - gap * 5) / 6;
    for (let qualityId = 1; qualityId <= 6; qualityId += 1) {
      const qualityRect = {
        x: rect.x + 20 + (qualityId - 1) * (buttonWidth + gap),
        y: rect.y + 86,
        width: buttonWidth,
        height: 42,
      };
      const name =
        this.engine.content.dictionary.get("quality")?.get(qualityId) ??
        `品质${qualityId}`;
      const selected = this.bulkSaleQualityIds.has(qualityId);
      this.button(
        context,
        `bulk-sale:quality:${qualityId}`,
        qualityRect,
        `${selected ? "✓ " : ""}${name}`,
        () => {
          if (selected) this.bulkSaleQualityIds.delete(qualityId);
          else this.bulkSaleQualityIds.add(qualityId);
        },
        selected
          ? QUALITY_COLORS[qualityId - 1] ?? COLORS.gold
          : "#4b3d40",
      );
    }
    text(context, `将出售 ${quote.count} 件装备，获得 ${quote.gold} 金币`, rect.x + rect.width / 2, rect.y + 166, {
      align: "center",
      color: quote.count > 0 ? COLORS.text : COLORS.muted,
      font: "700 16px sans-serif",
    });
    text(context, "锁定、已穿戴、极品词缀和零价值装备不会出售", rect.x + rect.width / 2, rect.y + 192, {
      align: "center",
      color: COLORS.muted,
      font: "500 11px sans-serif",
      maxWidth: rect.width - 36,
    });
    this.button(context, "bulk-sale:confirm", {
      x: rect.x + 20,
      y: rect.y + rect.height - 58,
      width: rect.width - 40,
      height: 42,
    }, quote.count > 0 ? `确认出售 ${quote.count} 件` : "没有符合条件的装备", () => {
      if (quote.count > 0) {
        this.engine.sellEquipmentBatch(quote.ids);
        this.inventoryPage = 0;
      }
      this.bulkSaleOpen = false;
    }, quote.count > 0 ? COLORS.gold : "#4b3d40");
    void snapshot;
  }

  private statSummary(item: V1DerivedEquipment): string {
    const values = [
      item.stats.attack ? `攻+${item.stats.attack}` : "",
      item.stats.defense ? `防+${item.stats.defense}` : "",
      item.stats.maxHp ? `生命+${item.stats.maxHp}` : "",
      item.stats.magicAttack ? `魔攻+${item.stats.magicAttack}` : "",
      item.stats.magicDefense ? `魔防+${item.stats.magicDefense}` : "",
    ].filter(Boolean);
    return `${values.join("  ") || "特殊装备"} · 价值 ${item.finalPrice}`;
  }

  private skillScopeLabel(scopeId: number): string {
    return ["无目标", "单个敌人", "全体敌人", "随机1人", "随机2人", "随机3人", "随机4人", "单个队友", "全体队友", "倒地队友", "全部倒地队友", "自身"][scopeId]
      ?? `目标类型 ${scopeId}`;
  }

  private compactSkillSummary(skill: V1Skill): string {
    if (skill.type === 2) return `${skill.name}（${skill.description || "被动常驻生效"}）`;
    const power = skill.powers[0] ?? 0;
    const repeats = Math.max(1, skill.repeats);
    const damage = power > 0
      ? `${power}倍攻击${repeats > 1 ? `×${repeats}次` : ""}`
      : "特殊效果";
    const cost = skill.costType === 1
      ? `MP ${skill.costs[0] ?? 0}`
      : skill.costType === 2
        ? `TP ${skill.costs[0] ?? 0}`
        : "无消耗";
    const cooldown = skill.cooldownTurns > 0 ? `冷却${skill.cooldownTurns}回合` : "无冷却";
    return `${skill.name}（${this.skillScopeLabel(skill.scopeId)}｜${damage}｜${cost}｜${cooldown}）`;
  }

  private skillEffectLines(skill: V1Skill): string[] {
    const type = skill.type === 2 ? "被动技能" : "主动技能";
    if (skill.type === 2) {
      const passiveStats = [
        ["生命", skill.maxHp], ["魔法", skill.maxMp],
        ["攻击", skill.attack], ["防御", skill.defense],
        ["魔攻", skill.magicAttack], ["魔防", skill.magicDefense],
        ["敏捷", skill.agility], ["幸运", skill.luck],
      ].filter((entry): entry is [string, number] => Number(entry[1]) !== 0)
        .map(([label, value]) => `${label} ${value > 0 ? "+" : ""}${value}`);
      const passiveStates = skill.passiveStates.map((stateId) => {
        const state = this.engine.content.states.find((entry) => entry.id === stateId);
        const details = state?.lifeSteal.map((entry) => {
          const resource = entry.channel === 1 ? "吸血" : "吸蓝";
          return entry.mode === 1 ? `${resource} ${entry.value}%` : `${resource} +${entry.value}/次`;
        }) ?? [];
        return `${state?.name ?? `状态${stateId}`}${details.length > 0 ? `（${details.join("、")}）` : ""}`;
      });
      const passiveEffects = [
        ...passiveStats,
        ...skill.traits.map((trait) => this.traitEffectSummary(trait)),
        ...passiveStates.map((name) => `常驻状态：${name}`),
      ];
      return [
        `${type} · 学会后常驻生效，无需在战斗中释放`,
        ...(passiveEffects.length > 0 ? passiveEffects : ["该技能没有主动消耗、伤害或冷却"]),
      ];
    }
    const costType = skill.costType === 1 ? "MP" : skill.costType === 2 ? "TP" : "无消耗";
    const cost = skill.costs[0] ?? 0;
    const hitType = skill.hitTypeId === 1 ? "物理命中" : skill.hitTypeId === 2 ? "魔法命中" : "必中";
    const damageType = ["无伤害", "HP伤害", "MP伤害", "HP恢复", "MP恢复", "HP吸取", "MP吸取"][skill.damageTypeId]
      ?? `伤害类型 ${skill.damageTypeId}`;
    const power = skill.powers[0] ?? 0;
    const formula = skill.formulaCode === 0
      ? damageType
      : `攻击×${power}－防御＋魔攻－魔防`;
    const effectLines = skill.effects.map((effect) => {
      const stateId = effect.params[0] ?? 0;
      const stateName = this.engine.content.states.find((state) => state.id === stateId)?.name ?? `状态${stateId}`;
      if (effect.code === 21) return `附加状态：${stateName}（${effect.params[2] ?? 100}%）`;
      if (effect.code === 22) return `解除状态：${stateName}`;
      return `附加效果 ${effect.code}：${effect.params.join("/")}`;
    });
    const targetLabel = skill.id === this.engine.content.rules.basicAttackSkillId
      ? "单个敌人（普通攻击）"
      : this.skillScopeLabel(skill.scopeId);
    const costLabel = costType === "无消耗" ? costType : `${costType} ${cost}`;
    return [
      `${type} · 目标：${targetLabel}`,
      `命中：${hitType} ${skill.successRatePercent}% · 消耗：${costLabel}`,
      `伤害公式：${formula}`,
      `攻击次数：${Math.max(1, skill.repeats)} · 冷却：${skill.cooldownTurns > 0 ? `${skill.cooldownTurns} 回合` : "无"} · 暴击：${skill.critical ? "可以" : "不可"}`,
      `伤害波动：±${skill.variancePercent}%${skill.requiredWeaponTypes.length > 0 ? ` · 武器要求：${skill.requiredWeaponTypes.join("/")}` : ""}`,
      ...effectLines,
    ];
  }

  private drawSkills(context: CanvasRenderingContext2D, snapshot: V1GameSnapshot): void {
    const rect = this.modalRect(780, 640);
    panel(context, rect, "rgba(20,15,21,.99)", COLORS.gold, 16);
    text(context, "已学技能 · 点击查看详情", rect.x + 24, rect.y + 40, { color: COLORS.gold, font: "700 21px serif" });
    this.button(context, "skills:close", { x: rect.x + rect.width - 54, y: rect.y + 12, width: 38, height: 32 }, "×", () => this.engine.closePanel());
    const skills = snapshot.player.learnedSkills.map((id) => this.engine.content.indexes.skills.get(id)).filter(Boolean);
    if (skills.length === 0) {
      text(context, "尚未学会技能，可通过技能书或升级获得。", rect.x + 24, rect.y + 88, { color: COLORS.muted, font: "500 14px sans-serif" });
      return;
    }
    if (!skills.some((skill) => skill?.id === this.selectedSkillId)) this.selectedSkillId = skills[0]?.id ?? null;
    const pageSize = 6;
    const pageCount = Math.max(1, Math.ceil(skills.length / pageSize));
    this.skillPage = Math.max(0, Math.min(pageCount - 1, this.skillPage));
    const visibleSkills = skills.slice(this.skillPage * pageSize, (this.skillPage + 1) * pageSize);
    const gap = 8;
    const cardWidth = (rect.width - 48 - gap) / 2;
    visibleSkills.forEach((skill, index) => {
      if (!skill) return;
      const column = index % 2;
      const rowIndex = Math.floor(index / 2);
      const row = { x: rect.x + 24 + column * (cardWidth + gap), y: rect.y + 62 + rowIndex * 57, width: cardWidth, height: 49 };
      const selected = skill.id === this.selectedSkillId;
      panel(context, row, selected ? "rgba(75,52,36,.98)" : COLORS.panelSoft, selected ? COLORS.gold : "#5b493e", 8);
      drawAtlas(context, this.atlases.skills[skill.iconSheet - 1], skill.iconIndex, { x: row.x + 5, y: row.y + 5, width: 39, height: 39 });
      text(context, skill.name, row.x + 51, row.y + 21, { color: selected ? COLORS.gold : COLORS.text, font: "700 13px sans-serif", maxWidth: row.width - 57 });
      const targetLabel = skill.id === this.engine.content.rules.basicAttackSkillId
        ? "单个敌人"
        : this.skillScopeLabel(skill.scopeId);
      text(context, skill.type === 2 ? "被动 · 常驻" : `${targetLabel} · ${skill.costType === 1 ? `MP ${skill.costs[0] ?? 0}` : skill.costType === 2 ? `TP ${skill.costs[0] ?? 0}` : "无消耗"}`, row.x + 51, row.y + 39, { color: COLORS.muted, font: "500 10px sans-serif", maxWidth: row.width - 57 });
      this.addHit(`skills:select:${skill.id}`, row, () => { this.selectedSkillId = skill.id; });
    });
    if (pageCount > 1) {
      this.button(context, "skills:previous", { x: rect.x + rect.width - 142, y: rect.y + 226, width: 48, height: 26 }, "‹", () => { this.skillPage = (this.skillPage + pageCount - 1) % pageCount; });
      text(context, `${this.skillPage + 1}/${pageCount}`, rect.x + rect.width - 82, rect.y + 246, { align: "center", color: COLORS.muted, font: "600 11px sans-serif" });
      this.button(context, "skills:next", { x: rect.x + rect.width - 62, y: rect.y + 226, width: 48, height: 26 }, "›", () => { this.skillPage = (this.skillPage + 1) % pageCount; });
    }
    const selected = skills.find((skill) => skill?.id === this.selectedSkillId);
    if (!selected) return;
    const detail = { x: rect.x + 24, y: rect.y + 262, width: rect.width - 48, height: rect.height - 282 };
    panel(context, detail, "rgba(31,24,32,.98)", "#7e5b3b", 11);
    drawAtlas(context, this.atlases.skills[selected.iconSheet - 1], selected.iconIndex, { x: detail.x + 14, y: detail.y + 14, width: 58, height: 58 });
    text(context, selected.name, detail.x + 86, detail.y + 36, { color: COLORS.gold, font: "700 21px serif", maxWidth: detail.width - 104 });
    text(context, selected.type === 2 ? "被动技能" : "主动技能 · 当前技能等级 1", detail.x + 86, detail.y + 59, { color: COLORS.muted, font: "600 12px sans-serif" });
    context.font = "500 13px 'PingFang SC','Microsoft YaHei',sans-serif";
    context.fillStyle = COLORS.text;
    this.skillEffectLines(selected).forEach((line, index) => {
      wrap(context, line, detail.x + 16, detail.y + 98 + index * 34, detail.width - 32, 18, 2);
    });
  }

  private drawWorldMap(context: CanvasRenderingContext2D): void {
    const rect = this.modalRect(760, 590);
    panel(context, rect, "rgba(20,15,21,.99)", COLORS.gold, 16);
    text(context, "39 张成长版网格地图", rect.x + 24, rect.y + 40, { color: COLORS.gold, font: "700 23px serif" });
    this.button(context, "world:close", { x: rect.x + rect.width - 54, y: rect.y + 12, width: 38, height: 32 }, "×", () => this.engine.closePanel());
    this.engine.content.maps.forEach((map, index) => {
      const columns = this.logicalHeight > this.logicalWidth ? 3 : 6;
      const cellWidth = (rect.width - 48 - (columns - 1) * 7) / columns;
      const row = Math.floor(index / columns);
      const column = index % columns;
      const item = { x: rect.x + 24 + column * (cellWidth + 7), y: rect.y + 66 + row * 62, width: cellWidth, height: 54 };
      panel(context, item, map.id === this.engine.currentMap().id ? "rgba(74,51,37,.96)" : "rgba(34,27,35,.9)", map.id === this.engine.currentMap().id ? COLORS.gold : "#4b3d40", 7);
      text(context, map.name, item.x + item.width / 2, item.y + 23, { align: "center", color: map.id === this.engine.currentMap().id ? COLORS.gold : COLORS.text, font: "700 11px sans-serif", maxWidth: item.width - 6 });
      text(context, `${map.width}×${map.height}`, item.x + item.width / 2, item.y + 42, { align: "center", color: COLORS.muted, font: "500 10px sans-serif" });
    });
  }

  private drawShop(context: CanvasRenderingContext2D, snapshot: V1GameSnapshot): void {
    const rect = this.modalRect(760, 580);
    panel(context, rect, "rgba(20,15,21,.99)", COLORS.success, 16);
    const shop = snapshot.activeShopId ? this.engine.content.indexes.shops.get(snapshot.activeShopId) : null;
    text(context, shop?.name ?? "商店", rect.x + 24, rect.y + 40, { color: COLORS.success, font: "700 23px serif" });
    text(context, `金币 ${snapshot.player.gold}`, rect.x + rect.width - 72, rect.y + 38, { align: "right", color: COLORS.gold, font: "700 14px sans-serif" });
    this.button(context, "shop:close", { x: rect.x + rect.width - 54, y: rect.y + 12, width: 38, height: 32 }, "×", () => this.engine.closePanel());
    if (!shop) return;
    const goods = shop.goods.filter((good) => good.entryType === 1);
    const columns = 2;
    const gap = 8;
    const cardHeight = this.logicalHeight > this.logicalWidth ? 72 : 76;
    const viewport = { x: rect.x + 24, y: rect.y + 66, width: rect.width - 66, height: rect.height - 90 };
    const visibleRows = Math.max(1, Math.floor((viewport.height + gap) / (cardHeight + gap)));
    const totalRows = Math.ceil(goods.length / columns);
    const maxScrollRow = Math.max(0, totalRows - visibleRows);
    this.shopScrollRow = Math.max(0, Math.min(maxScrollRow, this.shopScrollRow));
    const firstIndex = this.shopScrollRow * columns;
    const visibleGoods = goods.slice(firstIndex, firstIndex + visibleRows * columns);
    const cardWidth = (viewport.width - gap) / columns;
    visibleGoods.forEach((good, index) => {
      const item = this.engine.content.indexes.items.get(good.entryId);
      if (!item) return;
      const price = Math.ceil((good.priceOverride ?? item.buyPrice) * shop.buyRatePercent / 100);
      const column = index % columns;
      const rowIndex = Math.floor(index / columns);
      const row = { x: viewport.x + column * (cardWidth + gap), y: viewport.y + rowIndex * (cardHeight + gap), width: cardWidth, height: cardHeight };
      panel(context, row, COLORS.panelSoft, "#456d55", 8);
      drawAtlas(context, this.atlases.items, item.iconIndex, { x: row.x + 6, y: row.y + 8, width: 40, height: 40 }, 8, 8);
      text(context, item.name, row.x + 54, row.y + 24, { color: COLORS.text, font: "700 13px sans-serif", maxWidth: row.width - 62 });
      text(context, item.description, row.x + 54, row.y + 43, { color: COLORS.muted, font: "500 9px sans-serif", maxWidth: row.width - 62 });
      text(context, `${price} 金`, row.x + row.width - 10, row.y + row.height - 9, { align: "right", color: COLORS.gold, font: "700 12px sans-serif" });
      this.addHit(`shop:buy:${item.id}`, row, () => this.engine.buyItem(shop.id, item.id));
    });
    const track = { x: rect.x + rect.width - 22, y: viewport.y, width: 6, height: viewport.height };
    roundedPath(context, track, 3);
    context.fillStyle = "rgba(255,255,255,.10)";
    context.fill();
    const thumbHeight = maxScrollRow === 0 ? track.height : Math.max(36, track.height * visibleRows / Math.max(visibleRows, totalRows));
    const thumbY = maxScrollRow === 0 ? track.y : track.y + (track.height - thumbHeight) * this.shopScrollRow / maxScrollRow;
    roundedPath(context, { x: track.x, y: thumbY, width: track.width, height: thumbHeight }, 3);
    context.fillStyle = maxScrollRow > 0 ? COLORS.success : "#4b5a50";
    context.fill();
    if (maxScrollRow > 0) {
      this.addHit("shop:scroll-up", { x: track.x - 8, y: track.y, width: 22, height: track.height / 2 }, () => this.scrollShop(-1));
      this.addHit("shop:scroll-down", { x: track.x - 8, y: track.y + track.height / 2, width: 22, height: track.height / 2 }, () => this.scrollShop(1));
      text(context, `第 ${this.shopScrollRow + 1}/${maxScrollRow + 1} 页`, rect.x + rect.width - 68, rect.y + rect.height - 10, { align: "right", color: COLORS.muted, font: "500 9px sans-serif" });
    }
  }

  private scrollShop(deltaRows: number): void {
    const snapshot = this.engine.snapshot;
    const shop = snapshot.activeShopId ? this.engine.content.indexes.shops.get(snapshot.activeShopId) : null;
    const goodsCount = shop?.goods.filter((good) => good.entryType === 1).length ?? 0;
    const rect = this.modalRect(760, 580);
    const gap = 8;
    const cardHeight = this.logicalHeight > this.logicalWidth ? 72 : 76;
    const visibleRows = Math.max(1, Math.floor((rect.height - 90 + gap) / (cardHeight + gap)));
    const maxScrollRow = Math.max(0, Math.ceil(goodsCount / 2) - visibleRows);
    this.shopScrollRow = Math.max(0, Math.min(maxScrollRow, this.shopScrollRow + deltaRows));
  }

  private drawLoot(context: CanvasRenderingContext2D, snapshot: V1GameSnapshot): void {
    const loot = snapshot.loot;
    if (!loot) return;
    const rect = this.modalRect(700, 620);
    panel(context, rect, "rgba(20,15,21,.995)", loot.firstBossDefeated ? COLORS.danger : COLORS.gold, 18);
    const bossVictory = loot.sourceKind === "boss";
    text(context, bossVictory ? "首领战利品" : "战斗胜利", rect.x + rect.width / 2, rect.y + 52, {
      align: "center", color: bossVictory ? COLORS.danger : COLORS.gold, font: "700 30px 'Songti SC',serif",
    });
    text(context, `经验 +${loot.exp}   金币 +${loot.gold}`, rect.x + rect.width / 2, rect.y + 84, {
      align: "center", color: COLORS.text, font: "700 15px sans-serif",
    });
    text(context, `装备 ${loot.equipment.length} 件   物品 ${loot.items.reduce((sum, item) => sum + item.quantity, 0)} 件`, rect.x + rect.width / 2, rect.y + 112, {
      align: "center", color: COLORS.muted, font: "500 13px sans-serif",
    });
    const autoSaleQuote = this.engine.lootAutoSaleQuote();
    const autoSellQualityName = snapshot.autoSellMaxQualityId === 0
      ? "关闭"
      : this.engine.content.dictionary.get("quality")?.get(snapshot.autoSellMaxQualityId)
        ?? `品质${snapshot.autoSellMaxQualityId}`;
    const autoSellLabel = snapshot.autoSellMaxQualityId === 0
      ? "自动出售：关闭"
      : `自动出售：${autoSellQualityName}及以下${autoSaleQuote.count > 0 ? `（${autoSaleQuote.count}件 +${autoSaleQuote.gold}金）` : ""}`;
    this.button(context, "loot:auto-sell", {
      x: rect.x + rect.width / 2 - Math.min(220, rect.width / 2 - 24),
      y: rect.y + 124,
      width: Math.min(440, rect.width - 48),
      height: 32,
    }, autoSellLabel, () => this.engine.cycleAutoSellMaxQualityId(), COLORS.success);
    const rewards = [
      ...loot.items.map((entry) => ({ kind: "item" as const, entry })),
      ...loot.equipment.map((entry) => ({ kind: "equipment" as const, entry })),
    ];
    const columns = this.logicalHeight > this.logicalWidth ? 4 : 5;
    const visibleRewards = rewards.slice(0, columns * 4);
    visibleRewards.forEach((reward, index) => {
      const width = (rect.width - 48 - (columns - 1) * 8) / columns;
      const row = Math.floor(index / columns);
      const column = index % columns;
      const card = { x: rect.x + 24 + column * (width + 8), y: rect.y + 168 + row * 82, width, height: 74 };
      if (reward.kind === "item") {
        const item = this.engine.content.indexes.items.get(reward.entry.itemId);
        panel(context, card, COLORS.panelSoft, item?.type === 2 ? "#5d8fd8" : COLORS.border, 9);
        if (item) {
          drawAtlas(context, this.atlases.items, item.iconIndex, {
            x: card.x + card.width / 2 - 18, y: card.y + 4, width: 36, height: 36,
          }, 8, 8);
        }
        if (reward.entry.quantity > 1) {
          text(context, `×${reward.entry.quantity}`, card.x + card.width - 5, card.y + 16, {
            align: "right", color: COLORS.gold, font: "700 9px sans-serif",
          });
        }
        text(context, item?.name ?? `物品${reward.entry.itemId}`, card.x + card.width / 2, card.y + 54, {
          align: "center", color: item?.type === 2 ? "#8fbaff" : COLORS.text, font: "700 9px sans-serif", maxWidth: card.width - 6,
        });
        text(context, item?.type === 2 ? "技能书" : "物品", card.x + card.width / 2, card.y + 68, {
          align: "center", color: COLORS.muted, font: "500 7px sans-serif",
        });
        return;
      }
      const item = this.engine.deriveEquipment(reward.entry);
      panel(context, card, COLORS.panelSoft, QUALITY_COLORS[item.qualityId - 1] ?? COLORS.border, 9);
      drawAtlas(context, this.atlases.equipment, v1PrimaryEquipmentSlot(
        this.engine.content.indexes.equipment.get(item.equipmentId)?.equipmentType ?? 1,
      )?.atlasIndex ?? 0, {
        x: card.x + card.width / 2 - 18, y: card.y + 3, width: 36, height: 36,
      });
      this.drawExceptionalBadge(context, item, card, this.logicalHeight > this.logicalWidth);
      text(context, item.name, card.x + card.width / 2, card.y + 53, { align: "center", color: QUALITY_COLORS[item.qualityId - 1] ?? COLORS.text, font: "700 9px sans-serif", maxWidth: card.width - 6 });
      const autoSell = this.engine.willAutoSellLootEquipment(reward.entry);
      text(context, autoSell ? `自动出售 +${this.engine.equipmentSalePrice(item)}金` : this.statSummary(item), card.x + card.width / 2, card.y + 68, {
        align: "center", color: autoSell ? COLORS.gold : COLORS.muted, font: "500 7px sans-serif", maxWidth: card.width - 6,
      });
    });
    if (rewards.length > visibleRewards.length) {
      text(context, `另有 ${rewards.length - visibleRewards.length} 项奖励，收取后可在背包查看`, rect.x + rect.width / 2, rect.y + rect.height - 84, {
        align: "center", color: COLORS.muted, font: "500 11px sans-serif",
      });
    }
    const button = { x: rect.x + rect.width / 2 - 130, y: rect.y + rect.height - 72, width: 260, height: 50 };
    const collectLabel = autoSaleQuote.count > 0
      ? `收取并出售 ${autoSaleQuote.count} 件`
      : loot.firstBossDefeated ? "收下战利品并完成阶段" : "收下战利品";
    this.button(context, "loot:collect", button, collectLabel, () => this.engine.collectLoot(), loot.firstBossDefeated ? COLORS.danger : COLORS.gold);
  }

  private drawExceptionalBadge(
    context: CanvasRenderingContext2D,
    item: V1DerivedEquipment,
    rect: Rect,
    compact: boolean,
  ): void {
    if (!this.engine.isExceptionalEquipment(item)) return;
    const size = Math.max(12, Math.min(compact ? 16 : 18, rect.width * 0.42));
    const badge: Rect = {
      x: rect.x + rect.width - size - 2,
      y: rect.y + 2,
      width: size,
      height: size,
    };
    context.save();
    roundedPath(context, badge, Math.max(4, size * 0.3));
    context.fillStyle = "#d73d82";
    context.fill();
    context.lineWidth = 1;
    context.strokeStyle = "#ffd0e5";
    context.stroke();
    context.fillStyle = "#fff8fb";
    context.font = `800 ${Math.max(8, size * 0.62)}px 'PingFang SC',sans-serif`;
    context.textAlign = "center";
    context.textBaseline = "middle";
    context.fillText(
      "极",
      badge.x + badge.width / 2,
      badge.y + badge.height / 2 + 0.5,
    );
    context.restore();
  }

  private drawDialogue(context: CanvasRenderingContext2D, snapshot: V1GameSnapshot): void {
    const dialogue = snapshot.dialogue;
    if (!dialogue) return;
    const portrait = this.logicalHeight > this.logicalWidth;
    const rect = portrait
      ? { x: 14, y: this.logicalHeight - 260, width: this.logicalWidth - 28, height: 230 }
      : { x: 120, y: this.logicalHeight - 220, width: this.logicalWidth - 240, height: 170 };
    panel(context, rect, "rgba(16,12,17,.99)", COLORS.gold, 14);
    text(context, dialogue.speaker, rect.x + 22, rect.y + 34, { color: COLORS.gold, font: "700 17px serif" });
    context.font = "500 15px 'PingFang SC',sans-serif";
    context.fillStyle = COLORS.text;
    wrap(context, dialogue.lines.join("\n"), rect.x + 22, rect.y + 68, rect.width - 44, 24, portrait ? 5 : 4);
    text(context, "点击或按 E 关闭", rect.x + rect.width - 20, rect.y + rect.height - 16, { align: "right", color: COLORS.muted, font: "500 11px sans-serif" });
    this.addHit("dialogue:close", rect, () => this.engine.closeDialogue());
  }

  private drawToast(context: CanvasRenderingContext2D, value: string): void {
    const width = Math.min(this.logicalWidth - 30, Math.max(260, value.length * 16 + 42));
    const rect = { x: this.logicalWidth / 2 - width / 2, y: 12, width, height: 42 };
    panel(context, rect, "rgba(18,14,18,.98)", COLORS.gold, 10);
    text(context, value, rect.x + rect.width / 2, rect.y + 27, { align: "center", color: COLORS.text, font: "600 13px sans-serif", maxWidth: rect.width - 24 });
  }
}
